import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from './client'
import { useAuth } from '@/auth/useAuth'
import type { ReputationDto, SeasonDto } from './types'

export interface Island {
  id: number
  name: string
  type: string
  typeLabel: string
  x: number
  y: number
  qgLevel: number
}

export interface ShipCombatStatsDto {
  /** Dice this ship contributes per phase. */
  manoeuvre: number
  bordee: number
  abordage: number
  /** Pierce threshold: an attacker must reach it to damage the hull. */
  armure: number
  /** Hull points: hits absorbed before sinking. */
  hp: number
  /**
   * Crew complement. Not a combat stat: it is what the hull costs to FEED on a
   * mission (ration = crew × hours, see {@link estimateMissionRhum}). Sent by
   * ShipOutput so the fleet readout can show it — it used to be server-only,
   * which made the rhum bill impossible for a captain to work out.
   */
  crew: number
}

/** Occupation du pool de slots GLOBAL (modules équipés vs total de la coque). */
export interface ShipModuleSlotInfo {
  used: number
  total: number
}

/** A module copy currently fitted on a ship (one entry per equipped instance). */
export interface EquippedModuleDto {
  instanceId: number
  type: string
  label: string
  category: 'combat' | 'utility'
  phase: string | null
}

export interface ShipDto {
  id: number
  name: string
  shipType: string
  shipTypeLabel: string
  status: 'under_construction' | 'docked' | 'in_mission' | 'damaged' | 'lost' | 'refitting'
  statusLabel: string
  pc: number
  pf: number
  me: number
  vm: number
  cs: number
  /** Effective physical hold = base `cs` × the equipped hold modules (additive: each Cale renforcée +20%, so 2× = ×1.40). Shown on the fleet card; green when boosted. */
  cargoCapacity: number
  isUnderConstruction: boolean
  constructionResolvesAt: string | null
  /** When the ship's current mission resolves (ISO), or null if idle. */
  missionResolvesAt: string | null
  /** Effective profile: module dice + the permanent Forge hull bonus on `hp` — what combat actually uses. */
  combatStats: ShipCombatStatsDto
  /** Part of `combatStats.hp` from the Forge Charpente techs (+1/+2 per hull type) — 0 without research. Drives the green hull readout. */
  forgeBonusHp: number
  /**
   * Branle-bas (fiche #27): this hull holds the ranks, so it defends the island
   * and cannot leave on a mission. A docked hull the Chantier naval cap left
   * behind stays false: it is at anchor, out of the ranks, and does NOT fight.
   *
   * Optional on the TS side only. The server always sends it (ShipOutput), but
   * leaving it optional keeps the many launcher-test fixtures compiling without
   * each having to restate a flag they never exercise.
   */
  mobilized?: boolean
  /** Modules currently equipped (one entry per fitted copy). */
  modules: EquippedModuleDto[]
  /** Occupation du pool de slots global (modules équipés vs total de la coque). */
  slots: ShipModuleSlotInfo
  /** When the in-progress refit resolves (ISO), or null. */
  refitResolvesAt: string | null
  /** The refit currently under way (equip/unequip/swap), or null. */
  refit: { action: 'equip' | 'unequip' | 'swap'; type: string; label: string } | null
  /** Resource cost to equip ANY module on this hull (per the ship's class). Unequip is free. */
  refitCost: { gold: number; wood: number }
  /** Hull condition (0-100). Drops on combat, restored by repair. 100 = intact. */
  hullIntegrity: number
  /** False once hullIntegrity falls to/below the unseaworthy threshold — the ship can't launch until repaired. */
  seaworthy: boolean
  /** Wood cost to fully repair this hull right now — 0 when intact. */
  repairCostWood: number
}

export interface ShipBlueprintDto {
  type: string
  typeLabel: string
  cost: { gold: number; wood: number; rhum: number }
  timeSeconds: number
  requiredShipyardLevel: number
  unlocked: boolean
  combatStats: { manoeuvre: number; bordee: number; abordage: number; armure: number; hp: number; cargo: number; crew: number }
}

/**
 * Crew complement used to estimate rhum cost on operational missions —
 * mirrors ShipBlueprint::crewSize on the backend. Kept synced manually
 * for the small client-side estimate ("≈ N rhum"); the authoritative debit
 * happens server-side at launch.
 */
export const SHIP_CREW_SIZE: Record<string, number> = {
  brick: 5,
  fregate: 15,
  galion: 40,
  legendary: 30,
}

/**
 * Base cargo hold (cs) per ship type — mirrors ShipBlueprint::baseStats()['cs'].
 * The reference base hold; the actual ration is bounded by each ship's EFFECTIVE
 * hold (base × hold module), which the API sends as `ShipDto.cargoCapacity`.
 */
export const SHIP_CARGO_SIZE: Record<string, number> = {
  brick: 75,
  fregate: 150,
  galion: 375,
  legendary: 600,
}

/** A ship as far as rhum rationing cares: its type (crew size) and effective hold. */
export interface RhumShip {
  shipType: string
  /** Effective hold = base cs × the equipped hold module (mirrors ShipCargo::effective). */
  cargoCapacity: number
}

export function estimateMissionRhum(ships: RhumShip[], durationSeconds: number): number {
  if (durationSeconds <= 0 || ships.length === 0) return 0
  const hours = durationSeconds / 3600
  let rations = 0
  for (const s of ships) {
    // Mirror RhumCost::forMission: each hull rations crew × hours, capped at ITS
    // OWN effective hold — a Cale renforcée enlarges the hull, so a reinforced
    // ship rations more grog before the cap bites. The cap is per ship and not
    // pooled over the convoy: a pooled ceiling let one big hold raise the bill
    // for everyone (adding a Légendaire cost +600 rhum instead of its own
    // 30×hours). Floats are summed BEFORE the single ceil so a short hop with
    // many small hulls still rounds once.
    rations += Math.min((SHIP_CREW_SIZE[s.shipType] ?? 5) * hours, s.cargoCapacity)
  }
  return Math.ceil(rations)
}

/**
 * Commerce/smuggle carrying cap = effective hold × the account's Forge cargo
 * multiplier — mirrors ShipCargo::tradeCap (server). The +1e-7 absorbs IEEE-754
 * noise (e.g. 360 × 1.15 = 413.9999…) so the launcher gate matches the server's
 * accepted cap to the exact unit. Pass one ship's cargoCapacity, or the Σ of a
 * convoy's, as `effectiveCargo`.
 */
export function tradeCargoCap(effectiveCargo: number, forgeCargoMult: number): number {
  return Math.floor(effectiveCargo * forgeCargoMult + 1e-7)
}

/**
 * Floor estimate of the gold an exploration pays out — mirrors the server's
 * ExplorationReward::forTrip (40 + 4·leagues) at the BASE multiplier. The real
 * payout is ≥ this: the island's hidden type (mysterious/storm/lair pay 1.2–1.6×)
 * and any Forge economy bonus only push it up. One-way distance, like the server.
 * Replaces the old flat "+50 or" button label that ignored distance entirely.
 */
export function estimateExplorationGold(distanceLeagues: number): number {
  return Math.round(40 + 4 * Math.max(0, distanceLeagues))
}

export interface ShipyardDto {
  level: number
  slots: number
  slotsUsed: number
  buildable: ShipBlueprintDto[]
}

export interface ResourcesDto {
  gold: number
  wood: number
  rum: number
  /** EFFECTIVE hourly rate: negative on rum while the branle-bas drains the cellar. */
  goldPerHour: number
  woodPerHour: number
  rumPerHour: number
  capacity: number
  /**
   * Branle-bas de combat (fiche #27). Optional on the TS side so the many
   * existing fixtures keep compiling; the server always sends all three.
   */
  mobilized?: boolean
  /**
   * GROSS production, before upkeep. The `*PerHour` fields above are NET (what
   * actually accrues), so both are needed to break down
   * "production − branle-bas = net" instead of showing a bare negative number.
   */
  goldProductionPerHour?: number
  rumProductionPerHour?: number
  /** Seconds before the first of the two reserves runs dry. */
  autonomySeconds?: number | null
  /** Share of the autonomy gauge still full, 0..1, on a 48 h absolute window. */
  autonomyGauge?: number
}

export interface BuildingDto {
  id: number
  type: string
  typeLabel: string
  description: string
  level: number
  productionPerHour: Record<string, number>
  storageCapacity: number
  nextLevelProductionPerHour: Record<string, number>
  nextLevelStorageCapacity: number
  /** Coût NOMINAL du niveau suivant — affiché barré quand un créneau est déjà occupé. */
  nextUpgradeCost: { gold: number; wood: number }
  nextUpgradeTimeSeconds: number
  isUpgrading: boolean
  upgradeResolvesAt: string | null
  /** Coût RÉELLEMENT débité compte tenu des créneaux occupés (= nominal si aucun autre chantier). Calculé serveur. */
  nextUpgradePremiumCost: { gold: number; wood: number }
  /** Multiplicateur appliqué au nominal (1 = prix plein tarif, 1.5 = deuxième front…). */
  slotPremiumMultiplier: number
}

export interface BuildingProjectionRow {
  level: number
  productionPerHour: Record<string, number>
  storageCapacity: number
  shipyardSlots: number
  /** Créneaux de chantier accordés à ce niveau (Quartier-Général uniquement, 0 sinon). */
  upgradeSlots: number
  /** Mouillages accordés à ce niveau (Capitainerie uniquement, 0 sinon). */
  capitainerieBerths: number
  /** Taux de carénage à quai à ce niveau (Chantier naval uniquement, ratio PV/h — 0.02 = 2 %/h). */
  careenRatePerHour: number
  upgradeCost: { gold: number; wood: number }
  upgradeTimeSeconds: number
}

export interface BuildingBlueprintDto {
  type: string
  typeLabel: string
  description: string
  maxLevel: number
  projection: BuildingProjectionRow[]
}

export interface FleetResponse {
  island: Island | null
  resources: ResourcesDto | null
  shipyard: ShipyardDto | null
  ships: ShipDto[]
  buildings: BuildingDto[]
  /** Mouillages de la Capitainerie : coques possédées (hors épaves, constructions incluses) vs total. */
  berths: { used: number; total: number } | null
  /**
   * Créneaux de chantier : fronts ouverts vs créneaux accordés par le QG (le 2e au QG 18).
   * Pool distinct des navires et des défenses. `nextPremiumMultiplier` est le multiplicateur
   * de prix du prochain chantier lancé, calculé serveur (le front n'en fait qu'un miroir).
   */
  constructionSlots: { used: number; total: number; nextPremiumMultiplier: number } | null
  /** Account-wide Forge cargo multiplier (1.0 with no cargo tech) — the map launchers scale each ship's effective hold by this to match the server's commerce cap. */
  cargoForgeMult: number
  /** Account-wide Forge travel-speed multiplier (1.0 with no "Voilure perfectionnée" tech) — the map launchers fold it into the pre-launch ETA so the estimate reacts to the Forge speed bonus like the server's stored duration. */
  travelSpeedForgeMult: number
}

export interface FleetTypeCount {
  shipType: string
  shipTypeLabel: string
  count: number
}

export interface MissionDto {
  id: number
  type: string
  typeLabel: string
  status: 'in_progress' | 'completed' | 'cancelled' | 'failed'
  statusLabel: string
  startedAt: string
  resolvesAt: string
  completedAt: string | null
  shipId: number | null
  shipName: string | null
  shipType: string | null
  shipCount: number
  /** Composition de la flotte engagée, groupée par type. */
  fleet: FleetTypeCount[]
  destinationIslandId: number | null
  destinationIslandName: string | null
  destinationIslandTypeLabel: string | null
  payload: {
    shipName?: string
    durationMinutes?: number
    cargo?: { wood: number; rum: number }
    /** Chasse légendaire : la cible n'est pas une île, ses coordonnées sont figées ici au lancement. */
    targetX?: number
    targetY?: number
    archetypeKey?: string
    archetypeLabel?: string
    attemptsLeft?: number
    /**
     * Convoy ship ids as committed at launch (raid/trade/smuggle/salvage fleets).
     * Feeds the « Relancer la même mission » shortcut; legacy solo missions only
     * have the top-level shipId.
     */
    fleetShipIds?: number[]
    /**
     * Buy-run launch params: which resource was purchased with gold + how much
     * (vs cargo for a sell). `wood` is the legacy amount field kept for in-flight
     * missions launched before the resource generalisation.
     */
    buy?: { resource?: 'wood' | 'rum'; amount?: number; wood?: number }
    result?: {
      outcome: string
      title: string
      message: string
      goldEarned?: number
      /**
       * Crew's flat share of the mission gold, for display. For trade/explo the
       * net banked is `goldEarned − crewWage` (goldEarned is gross). For a raid the
       * wage is taken on the gold-equivalent total and EVAPORATED server-side, so
       * `loot` is ALREADY net — render it as-is, never subtract the wage again.
       */
      crewWage?: number
      // Buy-run: gold paid + which resource was bought (ResourceType value:
      // 'wood' | 'rum') and how much was bought / actually stored / lost at sea
      // (rendered as icons keyed off boughtResource).
      goldSpent?: number
      boughtResource?: 'wood' | 'rum'
      boughtAmount?: number
      storedAmount?: number
      lostAmount?: number
      /**
       * Loot/sale gold lost at sea because the warehouse was full when the
       * mission credited home (per-resource clamp) — every crediting mission
       * reports it (raid, salvage, sells, exploration…) EXCEPT the buy runs,
       * which carry the same information as storedAmount/lostAmount above.
       */
      overflowLost?: { gold: number; wood: number; rhum: number }
      /**
       * Recall only (outcome 'cancelled'): what the recalled hold actually put
       * back on the dock. A sell run's unsold cargo and a buy run's unspent gold
       * sail home with the hulls; the crew's rhum ration never does (drunk at
       * sea), so it is absent here by design. All-zero for every mission type
       * that carried nothing.
       */
      recovered?: { gold: number; wood: number; rhum: number }
      // Raid-only: plundered loot (rendered as icons in the report).
      loot?: { gold: number; wood: number; rhum: number }
      /**
       * Chasse légendaire seulement (fiche 32). `damageDealt` est un SCORE, pas
       * un progrès : la bête se regénère entre deux tentatives, rien ne se
       * reporte. `fleetAnnihilated` est vrai dès qu'elle survit, et il vaut
       * alors la flotte ENTIÈRE, survivants du combat compris.
       */
      damageDealt?: number
      coreMaxHp?: number
      coreHpAtEnd?: number
      felled?: boolean
      escaped?: boolean
      relocated?: boolean
      cornered?: boolean
      fleetAnnihilated?: boolean
      engagements?: number
      attemptsLeft?: number
      archetypeKey?: string
      archetypeLabel?: string
      trophy?: { prestige: number; titleKey: string; titleLabel: string } | null
      // Raid-only: full fleet-battle replay (M6-A5).
      combat?: CombatReplayDto
      lostShips?: string[]
      survivorCount?: number
      /**
       * Récif salvage only: pas une coque n'a regagné le port (le butin est
       * alors nul, il est ∝ à la cale des survivants). Le rapport titre en
       * rouge sur ce cas. Distinct de `fleetAnnihilated` ci-dessus, qui est
       * propre à la chasse légendaire et vrai dès que la bête survit.
       */
      fleetLost?: boolean
      /**
       * Commerce/récif salvage only (Mer dangereuse): a seeded NPC pirate
       * ambush gate fired on the return leg. The player's fleet is the
       * DEFENDER in `combat` (opposite of raids, where the player attacks).
       * Absent entirely when the gate didn't fire — never present as null.
       */
      ambush?: {
        occurred: true
        outcome: 'repelled' | 'defeated'
        danger: number
        combat: CombatReplayDto
        lostShips: string[]
        wreckPrizeGold: number
      }
      /**
       * Smuggling (sell) / marché noir (buy) only: a Marine patrol tried to
       * intercept the convoy. Same defender-perspective note as `ambush`.
       * No wreck prize (that's an ambush-only reward).
       */
      interception?: {
        occurred: true
        outcome: 'escaped' | 'confiscated'
        danger: number
        combat: CombatReplayDto
        lostShips: string[]
      }
      // Flavour event encountered on a long crossing (kraken, storm…).
      voyage?: {
        key: string
        label: string
        message: string
        effect: 'narrative' | 'cargo_loss' | 'gold_cost' | 'gold_find'
        amount: number
      }
      // Random "sea find" that dropped a fresh quest offer on the board — a
      // message in a bottle, a catch in the nets, a wreck… (questTitle names
      // the offer now waiting on the Quêtes page).
      bottle?: {
        key: string
        label: string
        message: string
        questTitle: string
      }
      // Reputation the mission moved (raid/commerce), with the resulting pavillon.
      reputation?: {
        loyaltyDelta: number
        methodDelta: number
        pavillon: string
        factionLabel: string
      } | null
    }
  }
}

export interface CombatEventDto {
  round: number
  phase: 'manoeuvre' | 'bordee' | 'abordage'
  side: 'attacker' | 'defender'
  dice: number
  hits: number
  sunk: string[]
  damaged: { name: string; damage: number }[]
  wasted: number
  /** Rerolls (Tour de guet + Forge) spent / converted this roll. Optional: pre-existing replays lack it. */
  rerolls?: number
  rerollHits?: number
}

export interface CombatFleetMemberDto {
  name: string
  type: string
  hp: number
  maxHp: number
  sunk: boolean
}

export interface CombatReplayDto {
  winnerSide: 'attacker' | 'defender' | 'draw'
  endReason: string
  events: CombatEventDto[]
  attackerSurvivors: CombatFleetMemberDto[]
  defenderSurvivors: CombatFleetMemberDto[]
}

export interface MissionsResponse {
  missions: MissionDto[]
  /**
   * Authoritative: true once the one-shot reconnaissance patrol exists this
   * season. Use this (not `missions.some(type==='patrol')`) to gate onboarding —
   * the list is capped at 20, so an aged-out patrol would otherwise re-trigger it.
   */
  patrolDone: boolean
}

export interface LairThreatDto {
  manoeuvre: number
  bordee: number
  abordage: number
  /** Toughest hull to crack — the attacker needs pierce ≥ this. */
  maxArmure: number
  /** Highest armure their shots reach (best bordée/abordage). Your hulls with armure > this are untouchable. */
  offensivePierce: number
  shipCount: number
  hasFort: boolean
  speciality: 'bordee' | 'abordage' | 'balanced'
}

export interface MapIslandDto {
  /**
   * Paliers du Grand Phare érigés par le propriétaire cette saison, 0 si aucun.
   * Public à dessein : voir l'ouvrage d'un rival monter est ce qui fait d'un sink
   * de ressources une course.
   */
  ownerChantierTiers?: number
  id: number
  name: string
  type: string
  typeLabel: string
  x: number
  y: number
  isNpc: boolean
  isMine: boolean
  isDiscovered: boolean
  ownerPseudo: string | null
  npcDanger: number | null
  /** Whether this island can be raided (any discovered NPC except récifs). */
  raidable: boolean
  /** Public intel on a discovered raidable island's defending fleet. Null otherwise. */
  threat: LairThreatDto | null
  /**
   * Resources this port sells for gold (pre-distance per-unit base price), at a
   * discovered buyable port. Comptoir → bois ET rhum; scierie → bois only;
   * distillerie → rhum only; MarinePort / Mysterious / others → empty array.
   * Drives the front buy panel (which goods, the resource sub-toggle, base price).
   */
  buyOptions: { resource: 'wood' | 'rhum'; basePrice: number }[]
  /**
   * Production specialty of the island — 'wood' (scierie) or 'rhum' (distillerie).
   * Null for every non-Production type. Drives the dedicated marker icon on the
   * map. NOT to be confused with `threat.speciality` (the defending fleet's combat
   * style: bordée / abordage / balanced).
   */
  specialty: string | null
  /**
   * PvP pavillon of a PLAYER island: the owner's emergent faction key
   * ('marine'/'corsaire'/…). Null for every NPC island.
   */
  ownerFaction: string | null
  /** PvP notoriety rank of a PLAYER island's owner. Null for NPC islands. */
  ownerRank: number | null
  /**
   * Apparence cosmétique publique du propriétaire d'une île JOUEUR (blason sur
   * le marqueur, portrait + titre dans le panneau de détail). Null pour les PNJ
   * et les capitaines jamais passés par la cabine.
   */
  ownerAppearance: AppearanceDto | null
  /**
   * Why this PLAYER island is (not) raidable by the viewer — the shared PvpRules
   * verdict. The matching boolean lives in `raidable` above. Null for NPC islands.
   */
  raidableReason: 'ok' | 'self' | 'shielded' | 'immune' | 'too_weak' | null
  /** Whether the viewer has scouted this PLAYER island. False for NPC islands. */
  scouted: boolean
  /**
   * ISO timestamp of when the viewer's intel on this PLAYER island was last
   * frozen (the éclaireur's snapshot). Null when un-scouted or NPC. Drives the
   * "intel du JJ/MM" staleness marker next to the threat readout.
   */
  scoutedAt: string | null
  /**
   * Docked fleet seen on the LAST scout, and whether its crews were aboard
   * (branle-bas, fiche #27). Frozen with the rest of the intel, so read it
   * alongside `scoutedAt`: the fleet may have put back to sea since.
   *
   * Null when un-scouted AND when the intel predates the feature. Render nothing
   * in that case: a default "no ship" would be a lie.
   */
  fleetAtQuay?: {
    mobilized: boolean
    composition: { shipType: string; shipTypeLabel: string; count: number }[]
  } | null
  /** Remaining salvage charges of a discovered récif; null/absent otherwise. */
  salvageRemaining?: number | null
  /** Fixed expedition difficulty of a mysterious island (the 4 paliers); null otherwise. */
  expeditionTier?: 'tres_facile' | 'facile' | 'moyen' | 'difficile' | null
  /** Human label of expeditionTier ("Très facile" …); null otherwise. */
  expeditionTierLabel?: string | null
  /** Authored fleet recommendation for the tier; null otherwise. */
  expeditionFleetHint?: string | null
  /** Whether a story pool exists for this mysterious island (an expedition can be launched). */
  hasRecit?: boolean
}

/**
 * Hostile attacker descriptor returned alongside raids/attacks. NPC raids carry
 * a faction (`marine`/`pirate`); a resolved PvP raid carries `player` and uses
 * the attacking captain's pseudo as its `label`.
 */
export interface AttackerDto {
  key: 'marine' | 'pirate' | 'player'
  label: string
  emoji: string
}

/** Timing bucket for the next raid — exact when the Watchtower reveals it, else fuzzy. */
export interface RaidScheduleDto {
  /** True when `scheduledAt` is exposed (Watchtower revealed the precise ETA). */
  precise: boolean
  /** 'precise' (when precise) | 'imminent' | 'bientot' | 'lointain'. */
  bucket: 'precise' | 'imminent' | 'bientot' | 'lointain'
}

/** One ship class in the attacking fleet (Watchtower-revealed composition). */
export interface RaidCompositionEntryDto {
  type: string
  label: string
  count: number
}

/**
 * Niveaux de Tour de guet exigés par chaque palier d'intel, servis par le serveur
 * (`WatchtowerIntel::tiers`) pour que les mentions verrouillées annoncent le chiffre
 * exact au lieu d'un « améliore ta tour » sans repère. Jamais recopiés côté front :
 * un palier rééquilibré côté serveur change la phrase sans toucher au client.
 */
export interface IntelTiersDto {
  composition: number
  repel: number
  preciseTiming: number
}

/**
 * The next incoming NPC raid, with intel GATED by the Watchtower level. The floor
 * (attacker identity + a timing bucket) is always present; danger, composition,
 * repelEstimate and the precise ETA only appear once the Watchtower reveals them.
 * `moreIntelIfUpgraded` flags that something is still hidden → the UI nudges the
 * player to upgrade. See backend `DefenseStateOutput::nextRaid` / `WatchtowerIntel`.
 */
export interface NextRaidDto {
  /** Always present: who is coming. */
  attacker: AttackerDto
  /** Always present: current Watchtower level (drives the reveal tiers). */
  watchtowerLevel: number
  /** Always present: the level each gated tier requires, for the locked hints. */
  intelTiers: IntelTiersDto
  /** True while at least one intel tier is still hidden → show the locked hints. */
  moreIntelIfUpgraded: boolean
  /** Always present: precise flag + fuzzy bucket. */
  schedule: RaidScheduleDto
  /** Threat magnitude — null until the Watchtower reveals it. */
  danger: number | null
  /** Attacking fleet — null until the Watchtower reveals it. */
  composition: RaidCompositionEntryDto[] | null
  /** Defender win chance (0..1) — null until the Watchtower reveals it. */
  repelEstimate: number | null
  /** Precise ETA (ISO ATOM) — present only when `schedule.precise`. */
  scheduledAt: string | null
}

/**
 * Root-level incoming attack on the world map (origin tile + ETA), or null. The
 * intel is GATED by the Watchtower, mirroring `DefenseStateOutput::nextRaid`: the
 * floor (faction + origin + the line-drawing `eta` + a fuzzy timing `schedule`) is
 * always present, while `danger`, `composition` and `repelEstimate` only appear
 * once the Watchtower reveals them (null otherwise). `moreIntelIfUpgraded` flags
 * that something is still hidden → the panel surfaces the locked "upgrade your
 * Watchtower" nudges. See backend `WorldMapOutput::incomingAttack`.
 */
export interface IncomingAttackDto {
  faction: AttackerDto
  /** Threat magnitude — null until the Watchtower reveals it. */
  danger: number | null
  /** ISO (ATOM) departure time, or null for a legacy raid scheduled pre-feature. */
  startedAt: string | null
  /** ISO (ATOM) arrival time — always sent so the map line keeps drawing. */
  eta: string
  origin: { x: number; y: number }
  /** Precise flag + fuzzy timing bucket (always present). */
  schedule: RaidScheduleDto
  /** Watchtower level — optional until the map payload exposes recon gating. */
  watchtowerLevel?: number
  /** Level each gated tier requires — optional, same reason as `watchtowerLevel`. */
  intelTiers?: IntelTiersDto
  /** True while some intel is still hidden — drives the locked nudges. */
  moreIntelIfUpgraded?: boolean
  /** Attacking fleet — optional/null until the Watchtower reveals it. */
  composition?: RaidCompositionEntryDto[] | null
  /** Defender win chance (0..1) — optional/null until the Watchtower reveals it. */
  repelEstimate?: number | null
}

/**
 * One in-flight PvP raid heading for the viewer's island, detected by the
 * Watchtower. Recon is GATED exactly like the NPC `incomingAttack`: the floor
 * (who is coming — `attackerPseudo` — plus the `origin` tile for the map line and
 * a fuzzy timing `schedule`) is always present, while `danger`, `composition`,
 * `repelEstimate` and the precise ETA only appear once the Watchtower reveals
 * them (null otherwise). `moreIntelIfUpgraded` flags hidden intel → locked nudges.
 * See backend `PvpRaidThreatView::entry` / `WatchtowerIntel`.
 */
export interface IncomingPlayerRaidDto {
  /** Always present: the attacking captain's pseudo (null only for a legacy/orphan raid). */
  attackerPseudo: string | null
  /** Always present: the origin tile so the map can draw the incoming line. */
  origin: { x: number; y: number }
  /** Always present: current Watchtower level (drives the reveal tiers). */
  watchtowerLevel: number
  /** Always present: the level each gated tier requires, for the locked hints. */
  intelTiers: IntelTiersDto
  /** True while at least one intel tier is still hidden → show the locked hints. */
  moreIntelIfUpgraded: boolean
  /** Always present: precise flag + fuzzy bucket. */
  schedule: RaidScheduleDto
  /** Threat magnitude — null until the Watchtower reveals it. */
  danger: number | null
  /** Attacking fleet — null until the Watchtower reveals it. */
  composition: RaidCompositionEntryDto[] | null
  /** Defender win chance (0..1) — null until the Watchtower reveals it. */
  repelEstimate: number | null
  /** Precise ETA (ISO ATOM) — present only when `schedule.precise`. */
  scheduledAt: string | null
}

export interface WorldMapResponse {
  season: SeasonDto
  gridSize: number
  islands: MapIslandDto[]
  incomingAttack: IncomingAttackDto | null
  /** In-flight PvP raids targeting the viewer, Watchtower-gated. Empty when none. */
  incomingPlayerRaids: IncomingPlayerRaidDto[]
  /**
   * La bête de fin de saison, si le capitaine l'a fait remonter. Champ RACINE et
   * non une île : elle n'a ni brouillard, ni fiche, ni commerce, et elle change
   * de place quand elle replonge. Null tant qu'aucune chasse n'est ouverte, et
   * dès qu'elle est abattue ou échappée.
   */
  huntTarget: HuntTargetDto | null
  /** La carrière du Grand Chantier, ou null si l'écran est encore fermé. */
  quarrySite: QuarrySiteDto | null
}

/**
 * La carrière du Grand Chantier. Champ RACINE du payload de carte, comme la bête :
 * ce n'est pas une île. Null tant que le Grand Chantier est fermé au capitaine.
 */
export interface QuarrySiteDto {
  x: number
  y: number
  label: string
  distanceLeagues: number
}

export interface HuntTargetDto {
  archetypeKey: string
  label: string
  sighting: string
  x: number
  y: number
  attemptsLeft: number
  maxAttempts: number
  cornered: boolean
}

export function useWorldMap() {
  return useQuery<WorldMapResponse>({
    queryKey: ['world', 'map'],
    queryFn: () => apiFetch<WorldMapResponse>('/api/world/map'),
  })
}

/** Poll cadence (ms) for the fleet query while a build/construction resolves. */
export const FLEET_BUSY_REFETCH_MS = 10_000

/**
 * True while the fleet has work resolving server-side: a building upgrading or a
 * ship under construction. Drives a polling fallback on the fleet query so the
 * card clears once the worker completes the job — even if the Mercure
 * `building_upgraded` event is missed (otherwise it stays stuck on "Résolution…").
 */
export function fleetIsBusy(data: FleetResponse | undefined): boolean {
  if (!data) return false
  return (
    data.buildings.some((b) => b.isUpgrading) ||
    data.ships.some((s) => s.status === 'under_construction' || s.status === 'in_mission')
  )
}

export function useMyFleet(options: { enabled?: boolean } = {}) {
  return useQuery<FleetResponse>({
    queryKey: ['me', 'fleet'],
    queryFn: () => apiFetch<FleetResponse>('/api/me/fleet'),
    enabled: options.enabled ?? true,
    refetchInterval: (query) => (fleetIsBusy(query.state.data) ? FLEET_BUSY_REFETCH_MS : false),
    // Catch a ship freed/built while the tab was backgrounded (global default is false).
    refetchOnWindowFocus: true,
  })
}

/**
 * Poll cadence (ms) for the missions query, or false to stop. The cadence ADAPTS
 * to the nearest in-progress mission's deadline so the card leaves "Résolution…"
 * on its own within a second or two — even when the Mercure realtime event is
 * missed (prod SSE reconnect windows) and without a manual refresh:
 *
 *   - no mission in progress  → false (stop polling)
 *   - overdue (timer elapsed) → 5s, until the worker/Mercure lands the completion
 *   - otherwise               → poll ~1s after the deadline, clamped to [3s, 30s]
 *
 * The clamp keeps far-off missions at a calm 30s, then tightens as the deadline
 * nears, so a short 45s patrol no longer sits on "Résolution…" through a 30s gap
 * (the old code only switched to 5s AFTER a poll already observed the overdue
 * state — itself gated by the 30s cadence).
 */
export function missionsRefetchMs(
  missions: ReadonlyArray<{ status: string; resolvesAt: string }>,
  now: number,
): number | false {
  const inProgress = missions.filter((m) => m.status === 'in_progress')
  if (inProgress.length === 0) return false
  const nextResolve = Math.min(...inProgress.map((m) => new Date(m.resolvesAt).getTime()))
  const msUntil = nextResolve - now
  if (msUntil <= 0) return 5_000
  return Math.max(3_000, Math.min(30_000, msUntil + 1_000))
}

/**
 * The player's in-progress mission of a given TYPE toward a given island, if
 * any. One run per (island, type) at a time: the Map keeps the island selectable
 * but locks a second launch of the same kind while one is under way (the backend
 * rejects it too, 409). Returns the mission so the UI can name the ship at sea.
 */
export function ongoingMissionTo(
  missions: ReadonlyArray<MissionDto>,
  islandId: number,
  type: MissionDto['type'],
): MissionDto | null {
  return (
    missions.find(
      (m) => m.status === 'in_progress' && m.type === type && m.destinationIslandId === islandId,
    ) ?? null
  )
}

/** Convenience wrapper for exploration — see {@link ongoingMissionTo}. */
export function ongoingExplorationTo(
  missions: ReadonlyArray<MissionDto>,
  islandId: number,
): MissionDto | null {
  return ongoingMissionTo(missions, islandId, 'exploration')
}

export function useMyMissions() {
  return useQuery<MissionsResponse>({
    queryKey: ['me', 'missions'],
    queryFn: () => apiFetch<MissionsResponse>('/api/missions/me'),
    refetchInterval: (query) => missionsRefetchMs(query.state.data?.missions ?? [], Date.now()),
    // The global default is false, but async waits invite tab-switching: refetch
    // on return so a mission completed while backgrounded leaves "Résolution…" at once.
    refetchOnWindowFocus: true,
  })
}

export function useLaunchPatrol() {
  const qc = useQueryClient()
  return useMutation<MissionDto>({
    mutationFn: () =>
      apiFetch<MissionDto>('/api/missions/patrol', { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'missions'] })
      qc.invalidateQueries({ queryKey: ['me', 'objectives'] })
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] })
    },
  })
}

/**
 * Cancels an in-progress mission while the fleet is still outbound (server
 * enforces `now < midpoint` — see `isMissionCancellable`). Rhum is NOT
 * refunded; the ships turn back and sail home in the time already elapsed.
 * Invalidates both missions (status → cancelled, new resolvesAt) and fleet
 * (ships stay `in_mission` until the shortened trip resolves, but their ETA
 * changes) so both surfaces reflect the new timers immediately.
 */
export function useCancelMission() {
  const qc = useQueryClient()
  return useMutation<MissionDto, Error, number>({
    mutationFn: (id) =>
      apiFetch<MissionDto>(`/api/missions/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'missions'] })
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] })
    },
  })
}

export function useBuildingBlueprints(enabled = true) {
  return useQuery<{ buildings: BuildingBlueprintDto[] }>({
    queryKey: ['buildings', 'blueprints'],
    queryFn: () => apiFetch<{ buildings: BuildingBlueprintDto[] }>('/api/buildings/blueprints'),
    staleTime: Infinity, // static reference data
    enabled,
  })
}

export function useUpgradeBuilding() {
  const qc = useQueryClient()
  return useMutation<BuildingDto, Error, number>({
    mutationFn: (buildingId: number) =>
      apiFetch<BuildingDto>(`/api/buildings/${buildingId}/upgrade`, { method: 'POST' }),
    onSuccess: () => {
      // Refetch fleet: resources were debited and the building is now upgrading.
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] })
    },
  })
}

export function useConstructShip() {
  const qc = useQueryClient()
  return useMutation<ShipDto, Error, string>({
    mutationFn: (shipType: string) =>
      apiFetch<ShipDto>('/api/ships', {
        method: 'POST',
        body: JSON.stringify({ shipType }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] })
    },
  })
}

// -------- Modules illimités (débloquer un TYPE, l'équiper sans limite) --------

/**
 * One module TYPE in the catalog, with the player's recipe unlock state
 * (Forge tech or reputation latch). Modules are UNLIMITED: there are no owned
 * copies — once a type is unlocked the player equips it on as many ships as the
 * slots allow. The equip cost is per-hull (`ShipDto.refitCost`), not per-type.
 */
export interface ModuleCatalogDto {
  type: string
  label: string
  description: string
  category: 'combat' | 'utility'
  phase: string | null
  unlockSource: 'forge' | 'reputation'
  recipeUnlocked: boolean
  /** Whether several copies may be fitted on one ship (the percent-multiplier utility modules). */
  stackable: boolean
  /** Progress toward a reputation latch — null for Forge modules and once unlocked. */
  unlockProgress: ModuleUnlockProgressDto | null
}

/** How far the captain is from latching a reputation-gated recipe (the Faux-fond). */
export interface ModuleUnlockProgressDto {
  current: number
  required: number
  /** True for a Corsaire/Pirate (on the outlaw track); false for a lawful captain who must turn first. */
  outlawAligned: boolean
}

export interface ModuleCatalogResponse {
  catalog: ModuleCatalogDto[]
}

/** The full module catalog with the player's per-type unlock state this season. */
export function useModuleCatalog(enabled = true) {
  return useQuery<ModuleCatalogResponse>({
    queryKey: ['me', 'modules'],
    queryFn: () => apiFetch<ModuleCatalogResponse>('/api/me/modules'),
    enabled,
  })
}

/**
 * Equip a module TYPE onto a ship (kicks off a timed refit, debits the hull's
 * refit cost). When the target slot category is full, `removedInstanceId` names
 * the currently-equipped module to unfit first (a swap). Returns the fleet payload.
 */
export function useEquipModule() {
  const qc = useQueryClient()
  return useMutation<FleetResponse, Error, { shipId: number; type: string; removedInstanceId?: number }>({
    mutationFn: ({ shipId, type, removedInstanceId }) =>
      apiFetch<FleetResponse>(`/api/ships/${shipId}/modules`, {
        method: 'POST',
        body: JSON.stringify({ type, removedInstanceId }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] })
      qc.invalidateQueries({ queryKey: ['me', 'modules'] })
    },
  })
}

/** Remove an equipped module from a ship (timed refit, no cost). */
export function useUnequipModule() {
  const qc = useQueryClient()
  return useMutation<FleetResponse, Error, { shipId: number; instanceId: number }>({
    mutationFn: ({ shipId, instanceId }) =>
      apiFetch<FleetResponse>(`/api/ships/${shipId}/modules/${instanceId}/remove`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] })
      qc.invalidateQueries({ queryKey: ['me', 'modules'] })
    },
  })
}

export function useRenameShip() {
  const qc = useQueryClient()
  return useMutation<ShipDto, Error, { shipId: number; name: string }>({
    mutationFn: ({ shipId, name }) =>
      apiFetch<ShipDto>(`/api/ships/${shipId}/name`, {
        method: 'PATCH',
        body: JSON.stringify({ name }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'fleet'] }),
  })
}

/** Repair one docked ship's hull to 100 %, spending its `repairCostWood`. Returns the updated ship + fresh resources. */
export function useRepairShip() {
  const qc = useQueryClient()
  return useMutation<{ ship: ShipDto; resources: ResourcesDto }, Error, number>({
    mutationFn: (shipId: number) =>
      apiFetch<{ ship: ShipDto; resources: ResourcesDto }>(`/api/ships/${shipId}/repair`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'fleet'] }),
  })
}

/** Repair every damaged docked ship at once (atomic — repairs nothing if the wood is short). Returns the full fleet payload. */
export function useRepairAll() {
  const qc = useQueryClient()
  return useMutation<FleetResponse, Error, void>({
    mutationFn: () => apiFetch<FleetResponse>('/api/ships/repair-all', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'fleet'] }),
  })
}

// -------- Defenses / Fort + NPC raids (M-Defense P2) --------

export interface DefenseStatsDto {
  manoeuvre: number
  bordee: number
  abordage: number
  armure: number
  hp: number
}

export interface DefenseStructureDto {
  type: string // 'fort' | 'battery' | 'militia' | 'watchtower'
  label: string
  description: string
  iconHint: string // 'armure' | 'bordee' | 'abordage' | 'manoeuvre'
  level: number
  hqGate: boolean
  current: DefenseStatsDto | null
  next: DefenseStatsDto | null
  upgradeCost: { gold: number; wood: number; rhum: number } | null
  /** A construction timer is running for this structure (dedicated defence queue). */
  isUpgrading: boolean
  /** ISO timestamp the in-progress upgrade resolves at, or null. */
  upgradeResolvesAt: string | null
  /** Seconds the next upgrade would take. */
  upgradeTimeSeconds: number
}

/**
 * A resolved attack the island suffered, in "Dernières attaques". `source`
 * discriminates NPC raids (faction attacker + a `danger` tier) from PvP raids
 * (player attacker + `attackerShips`, the attacking fleet size; `danger` null).
 */
export interface DefenseRaidDto {
  id: number
  source: 'npc' | 'player'
  /** NPC danger tier; null for PvP raids (which use `attackerShips` as their magnitude). */
  danger: number | null
  /** Attacking fleet size for PvP raids; null for NPC raids. */
  attackerShips: number | null
  attacker: AttackerDto
  outcome: string | null
  resolvedAt: string | null
  loot: { gold: number; wood: number; rhum: number } | null
  sunkShips: string[]
  /**
   * Coques de la garnison que le plafond anti-grief a épargnées : renflouées au
   * quai, hors d'état de naviguer, mais PAS perdues. Vide sur les raids résolus
   * avant le plafond.
   */
  refloatedShips: string[]
  /**
   * Full seeded battle replay (same `FleetBattleResult::toArray()` shape as PvP
   * raids) — drives "Revivre le combat" in perspective défenseur. Null for
   * legacy raids resolved before replays were stored (hide the button).
   */
  combat: CombatReplayDto | null
}

/** One hull either at the ranks, or that sounding the branle-bas would call. */
export interface MobilizedShipDto {
  id: number | null
  name: string
  type: string
  typeLabel: string
}

/**
 * Upkeep tariff per hull RANK, so the panel can explain the cost without a total.
 * Indexed on the island's own production and reserve, so these numbers are a
 * projection of sounding right now, never a universal price list.
 */
export interface MobilizationTariffDto {
  type: string
  label: string
  rumPerHour: number
  goldPerHour: number
}

/**
 * Branle-bas de combat (fiche #27): whether the docked fleet is at the ranks,
 * what it costs per hour, and how long the cellar can pay for it.
 *
 * Everything here is recomputed server-side. `autonomySeconds` is the FIRST of
 * the two resources to run dry; when it elapses the mobilisation stands down on
 * its own and the bell rings, so a mobilisation can never be permanent.
 */
export interface MobilizationDto {
  active: boolean
  since: string | null
  /** When the ranks may be broken: the branle-bas is a firm commitment. */
  releaseAt: string | null
  /** False while the commitment window is still running — stand-down refused. */
  canStandDown: boolean
  shipsAtQuay: number
  /** Hull cap from the Chantier naval — half its level, at least one. */
  cap: number
  /** Docked hulls the cap left behind: they do NOT defend. */
  outOfRanks: number
  /** Hulls currently at the ranks (empty when at anchor). */
  engaged: MobilizedShipDto[]
  /** Hulls that sounding it would call right now (empty when already mobilised). */
  wouldEngage: MobilizedShipDto[]
  /** Rum drawn from the cellar per hour, on top of production being consumed. */
  rumPerHour: number
  /** Gold wage per hour. */
  goldPerHour: number
  /** Rum production suspended while mobilised — shown as suspended, not negative. */
  rumProductionSuspended: number
  /** Seconds before the first resource runs dry; null when nothing is engaged. */
  autonomySeconds: number | null
  endsAt: string | null
  minBilledHours: number
  minimumBill: { rum: number; gold: number }
  /** False when the minimum billed window cannot be covered — button disabled. */
  canAfford: boolean
  tariffs: MobilizationTariffDto[]
}

export interface DefenseStateResponse {
  hqLevel: number
  structures: DefenseStructureDto[]
  nextRaid: NextRaidDto | null
  /** In-flight PvP raids targeting this island, Watchtower-gated. Empty when none. */
  incomingPlayerRaids: IncomingPlayerRaidDto[]
  recentRaids: DefenseRaidDto[]
  mobilization: MobilizationDto | null
}

/** True while a defence is building server-side — drives a polling fallback. */
export function defenseIsBusy(data: DefenseStateResponse | undefined): boolean {
  return !!data?.structures.some((s) => s.isUpgrading)
}

export function useDefense(enabled = true) {
  return useQuery<DefenseStateResponse>({
    queryKey: ['me', 'defense'],
    queryFn: () => apiFetch<DefenseStateResponse>('/api/me/defense'),
    enabled,
    refetchInterval: (query) => (defenseIsBusy(query.state.data) ? FLEET_BUSY_REFETCH_MS : false),
    refetchOnWindowFocus: true,
  })
}

/** One level's forecast for a defence structure (combat profile + reroll budget + cost/time). */
export interface DefenseProjectionRow {
  level: number
  manoeuvre: number
  bordee: number
  abordage: number
  armure: number
  hp: number
  /** Watchtower reroll budget at this level (0 for the other structures). */
  rerolls: number
  upgradeCost: { gold: number; wood: number; rhum: number }
  upgradeTimeSeconds: number
}

export interface DefenseBlueprintDto {
  type: string // 'fort' | 'battery' | 'militia' | 'watchtower'
  typeLabel: string
  description: string
  maxLevel: number
  projection: DefenseProjectionRow[]
}

/** Static, player-agnostic level-by-level forecast for every defence structure. */
export function useDefenseBlueprints(enabled = true) {
  return useQuery<{ structures: DefenseBlueprintDto[] }>({
    queryKey: ['me', 'defense', 'blueprints'],
    queryFn: () => apiFetch<{ structures: DefenseBlueprintDto[] }>('/api/me/defense/blueprints'),
    staleTime: Infinity, // static reference data
    enabled,
  })
}

export function useUpgradeDefense() {
  const qc = useQueryClient()
  return useMutation<DefenseStateResponse, Error, string>({
    mutationFn: (structure) =>
      apiFetch<DefenseStateResponse>(`/api/me/defense/${structure}`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'defense'] })
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] }) // resources were debited
    },
  })
}

/**
 * Sound / break the branle-bas. Both invalidate the fleet too: mobilising locks
 * the engaged hulls out of missions and re-snapshots the island purse, so the
 * fleet page and the resource bar must both re-read.
 */
function useMobilizationMutation(method: 'POST' | 'DELETE') {
  const qc = useQueryClient()
  return useMutation<DefenseStateResponse, Error, void>({
    mutationFn: () => apiFetch<DefenseStateResponse>('/api/me/defense/mobilization', { method }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'defense'] })
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] })
      qc.invalidateQueries({ queryKey: ['me', 'resources'] })
    },
  })
}

export function useSoundMobilization() {
  return useMobilizationMutation('POST')
}

export function useStandDownMobilization() {
  return useMobilizationMutation('DELETE')
}

// -------- Objectives / quests (M8-Q) --------

export interface ObjectiveDto {
  key: string
  title: string
  description: string
  current: number
  target: number
  completed: boolean
  claimed: boolean
  reward: { gold: number; wood: number; rhum: number; prestige: number }
}

export function useObjectives(enabled = true) {
  return useQuery<{ objectives: ObjectiveDto[] }>({
    queryKey: ['me', 'objectives'],
    queryFn: () => apiFetch<{ objectives: ObjectiveDto[] }>('/api/me/objectives'),
    enabled,
  })
}

export function useClaimObjective() {
  const qc = useQueryClient()
  const { refreshUser } = useAuth()
  return useMutation<{ objectives: ObjectiveDto[] }, Error, string>({
    mutationFn: (key) =>
      apiFetch<{ objectives: ObjectiveDto[] }>(`/api/me/objectives/${key}/claim`, { method: 'POST' }),
    onSuccess: (data) => {
      qc.setQueryData(['me', 'objectives'], data)
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] }) // reward may credit resources
      qc.invalidateQueries({ queryKey: ['me', 'profile'] }) // captain season stats
      // Prestige lives on the auth user (AuthContext state, not a TanStack query),
      // so invalidating queries isn't enough — pull a fresh /api/me into context.
      void refreshUser()
    },
  })
}

// -------- Quêtes (Lot 2) --------

export interface QuestTaskDto {
  key: string
  kind: 'count' | 'accumulate' | 'streak' | 'threshold'
  verb: 'trade_buy' | 'trade_sell' | 'raid' | 'smuggle' | 'explore' | 'defend' | 'mission' | 'player_raid' | 'repel_player' | 'story'
  qualifier: Record<string, string | number>
  target: number
  progress: number
  done: boolean
}
export interface QuestRewardDto {
  loyaltyDelta: number
  methodDelta: number
  gold: number
  wood: number
  rhum: number
  woodChoice?: number // > 0 = claim requires a gold/wood choice
}
export interface QuestDto {
  id: number
  faction: 'neutral' | 'guilde' | 'marine' | 'corsaire' | 'pirate'
  status: 'offered' | 'active' | 'completed' | 'claimed' | 'refused' | 'expired'
  source: 'timed' | 'event'
  mode: 'set' | 'sequence'
  title: string
  body: string
  tasks: QuestTaskDto[]
  reward: QuestRewardDto
  deadline: string | null
  /**
   * Rite d'invocation de la chasse légendaire : le rendez-vous de fin de saison.
   * Ce n'est pas une quête de Taverne (elle ne vient pas du tableau, n'expire
   * jamais et ne paie rien de matériel), et la carte le signale comme tel.
   */
  isRite?: boolean
}
export interface TavernStatusDto {
  built: boolean
  level: number
  offersCap: number
  activeCap: number
  activeCount: number
  offerCooldownHours: number
  nextOfferAt: string | null
  rerollCost: number | null // null = exhausted for today
  rerollsUsedToday: number
  tourneeCostRhum: number
  tourneeMinLevel: number
  tourneeAvailableAt: string | null // null = available now (if level ok)
  unlockedFamilies: string[]
}
export interface QuestsResponse {
  offered: QuestDto[]
  active: QuestDto[]
  completed: QuestDto[]
  tavern: TavernStatusDto
}

export function useQuests(enabled = true) {
  return useQuery<QuestsResponse>({
    queryKey: ['me', 'quests'],
    queryFn: () => apiFetch<QuestsResponse>('/api/me/quests'),
    enabled,
  })
}

function useQuestTransition(action: 'accept' | 'refuse') {
  const qc = useQueryClient()
  return useMutation<{ quest: QuestDto }, Error, number>({
    mutationFn: (id) => apiFetch<{ quest: QuestDto }>(`/api/me/quests/${id}/${action}`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'quests'] }),
  })
}
export const useAcceptQuest = () => useQuestTransition('accept')
export const useRefuseQuest = () => useQuestTransition('refuse')

export function useClaimQuest() {
  const qc = useQueryClient()
  const { refreshUser } = useAuth()
  return useMutation<{ quest: QuestDto }, Error, { id: number; resource?: 'gold' | 'wood' }>({
    mutationFn: ({ id, resource }) =>
      apiFetch<{ quest: QuestDto }>(`/api/me/quests/${id}/claim`, {
        method: 'POST',
        ...(resource ? { body: JSON.stringify({ resource }) } : {}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'quests'] })
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] }) // resources credited (+ rep on fleet)
      qc.invalidateQueries({ queryKey: ['me', 'profile'] }) // captain rep + season stats
      void refreshUser() // reputation lives on the user
    },
  })
}

export function useAbandonQuest() {
  const qc = useQueryClient()
  return useMutation<{ quest: QuestDto }, Error, number>({
    mutationFn: (id) => apiFetch<{ quest: QuestDto }>(`/api/me/quests/${id}/abandon`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'quests'] }),
  })
}

export function useRerollQuest() {
  const qc = useQueryClient()
  return useMutation<{ quest: QuestDto }, Error, number>({
    mutationFn: (id) => apiFetch<{ quest: QuestDto }>(`/api/me/quests/${id}/reroll`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'quests'] })
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] }) // rhum débité
    },
  })
}

export function useOrderRareQuest() {
  const qc = useQueryClient()
  return useMutation<{ quest: QuestDto }, Error, void>({
    mutationFn: () => apiFetch<{ quest: QuestDto }>('/api/me/quests/order-rare', { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'quests'] })
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] })
    },
  })
}

// -------- Tavern blueprint (static reference data) --------

export interface TavernProgressionRow {
  level: number
  offersCap: number
  activeCap: number
  offerCooldownHours: number
  unlockedFamilies: string[]
  questRepMultiplier: number
}

export interface TavernBlueprintDto {
  buildCostGold: number
  buildTimeSeconds: number
  progression: TavernProgressionRow[]
}

export function useTavernBlueprint(enabled = true) {
  return useQuery<TavernBlueprintDto>({
    queryKey: ['tavern', 'blueprint'],
    queryFn: () => apiFetch<TavernBlueprintDto>('/api/tavern/blueprint'),
    staleTime: Infinity, // static reference data
    enabled,
  })
}

export function useBuildBuilding() {
  const qc = useQueryClient()
  return useMutation<unknown, Error, { type: string }>({
    mutationFn: (payload) =>
      apiFetch('/api/buildings/build', { method: 'POST', body: JSON.stringify(payload) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] })   // resources debited
      qc.invalidateQueries({ queryKey: ['me', 'quests'] })  // tavern status changes
    },
  })
}

// -------- Captain profile (prestige + reputation + season stats) --------

export interface CaptainProfileDto {
  pseudo: string
  createdAt: string
  prestigePoints: number
  /** Label du titre de série de connexions le plus élevé (ex. « Loup de mer »), null si aucun. */
  streakTitle: string | null
  /** Apparence cosmétique (cabine) — null tant que le joueur n'a rien configuré. */
  appearance: AppearanceDto | null
  reputation: ReputationDto
  season: SeasonDto | null
  stats: {
    goldBanked: number
    shipsBuilt: number
    islandsDiscovered: number
    raidsConducted: number
    tradeRunsConducted: number
    shipyardLevel: number
    hqLevel: number
    reputationRank: number
  }
}

export function useCaptainProfile(enabled = true) {
  return useQuery<CaptainProfileDto>({
    queryKey: ['me', 'profile'],
    queryFn: () => apiFetch<CaptainProfileDto>('/api/me/profile'),
    enabled,
  })
}

// -------- Cosmetics / cabine du capitaine (personnalisation, Lot 1) --------

/** Config d'avatar (slots camelCase → clé d'item du catalogue). Contrat = AppearanceService (backend). */
export interface AvatarConfigDto {
  face: string
  hair?: string | null
  facialHair?: string | null
  headgear?: string | null
  accessory?: string | null
  background: string
  frame?: string | null
}

export interface BlasonConfigDto {
  shape: string
  glyph: string
  palette: string
  field?: string | null
}

/** Apparence publique d'un capitaine (profils, classements) — null si jamais configurée. */
export interface AppearanceDto {
  avatar: AvatarConfigDto | null
  blason: BlasonConfigDto | null
  titleLabel: string | null
}

export interface CosmeticItemDto {
  key: string
  label: string
  category: string
  slot: string
  /**
   * Miroir de `App\Enum\CosmeticUnlockSource` : `hunt` (bête de saison abattue),
   * `chantier` (palier du Grand Phare) et `season` (podium d'une saison close)
   * existent côté serveur depuis leurs lots respectifs.
   */
  source:
    | 'base'
    | 'streak'
    | 'reputation'
    | 'objective'
    | 'prestige'
    | 'commemorative'
    | 'hunt'
    | 'chantier'
    | 'season'
  conditionLabel: string
  owned: boolean
  sort: number
}

export interface CosmeticsResponse {
  catalog: CosmeticItemDto[]
  avatarConfig: AvatarConfigDto | null
  blasonConfig: BlasonConfigDto | null
  equippedTitle: string | null
}

/** Payload PUT /api/me/appearance (revalidé serveur). */
export interface SaveAppearancePayload {
  avatarConfig: AvatarConfigDto | null
  blasonConfig: BlasonConfigDto | null
  equippedTitle: string | null
}

export function useMyCosmetics(enabled = true) {
  return useQuery<CosmeticsResponse>({
    queryKey: ['me', 'cosmetics'],
    queryFn: () => apiFetch<CosmeticsResponse>('/api/me/cosmetics'),
    enabled,
  })
}

export function useSaveAppearance() {
  const qc = useQueryClient()
  return useMutation<SaveAppearancePayload, Error, SaveAppearancePayload>({
    mutationFn: (payload) =>
      apiFetch<SaveAppearancePayload>('/api/me/appearance', {
        method: 'PUT',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'cosmetics'] })
      qc.invalidateQueries({ queryKey: ['me', 'profile'] }) // appearance du profil
    },
  })
}

export function useRenameIsland() {
  const qc = useQueryClient()
  return useMutation<{ id: number; name: string }, Error, { islandId: number; name: string }>({
    mutationFn: ({ islandId, name }) =>
      apiFetch<{ id: number; name: string }>(`/api/islands/${islandId}/name`, {
        method: 'PATCH',
        body: JSON.stringify({ name }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] })
      qc.invalidateQueries({ queryKey: ['world', 'map'] })
    },
  })
}

export interface TradeRunVars {
  shipIds: number[]
  destinationIslandId: number
  wood: number
  rum: number
}

export function useLaunchTradeRun() {
  const qc = useQueryClient()
  return useMutation<MissionDto, Error, TradeRunVars>({
    mutationFn: (vars) =>
      apiFetch<MissionDto>('/api/trade-runs', { method: 'POST', body: JSON.stringify(vars) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] })
      qc.invalidateQueries({ queryKey: ['me', 'missions'] })
    },
  })
}

export interface TradeEstimateDto {
  /** Legal sale value (base value × destination × distance), no reputation. */
  base: number
  /** Gold the player's faction affinity adds on top (method axis). */
  reputationBonus: number
  /** Rounded total = base + reputationBonus. */
  total: number
  /**
   * Ambush probability ∈ [0,1] the resolver will gate on at completion — SAME
   * formula, fed by the previewed destination danger + cargo fill. 0 when no
   * ambush is possible (destination danger ≤ 1).
   */
  ambushRisk?: number
  /**
   * Composition of the pirate party the ambush would field for this haul
   * (grouped by ship type), driven by the cargo's gold value — see
   * `SeaAmbush::ambushDangerForHaul` / `NpcRaidParty::forDanger`. Empty or
   * absent when no ambush is possible (ambushRisk 0).
   */
  ambushFleet?: { shipType: string; count: number }[]
  /**
   * Advisory win-odds tier comparing the player's SELECTED fleet to
   * `ambushFleet` (faible = the player dominates, forte = outgunned) — see
   * `AmbushThreat::tierFor`. Recomputes as the selected fleet/cargo changes.
   */
  ambushThreat?: 'faible' | 'modérée' | 'forte'
}

/**
 * Sale-gold breakdown for a prospective legal trade run, at the player's CURRENT
 * reputation. Replaces the former client-side estimate so the front no longer
 * mirrors balance constants — the server is authoritative.
 */
export function useTradeEstimate(
  shipIds: number[],
  destinationIslandId: number,
  wood: number,
  rum: number,
  enabled: boolean,
) {
  return useQuery<TradeEstimateDto>({
    // The sale gold breakdown depends only on cargo + destination + reputation
    // (the fleet doesn't affect it), but `ambushThreat` now compares the
    // SELECTED fleet's power to the ambush party (see AmbushThreat::tierFor) —
    // so shipIds must be part of the key, sorted so re-ordering the same
    // selection doesn't spuriously refetch.
    queryKey: ['trade', 'estimate', destinationIslandId, wood, rum, [...shipIds].sort((a, b) => a - b)],
    queryFn: () =>
      apiFetch<TradeEstimateDto>('/api/trade-runs/estimate', {
        method: 'POST',
        body: JSON.stringify({ shipIds, destinationIslandId, wood, rum }),
      }),
    enabled: enabled && shipIds.length > 0 && wood + rum > 0,
    staleTime: 60_000,
  })
}

export interface BuyGoodsVars {
  shipIds: number[]
  destinationIslandId: number
  /**
   * Which good to buy — matches BuyGoodsInput.resource (IslandSpecialty:
   * 'wood' at a scierie/comptoir, 'rhum' at a distillerie). Optional: omit to
   * default to wood server-side (back-compat with the wood-only comptoir panel).
   */
  resource?: 'wood' | 'rhum'
  /** Units to buy with gold (BuyGoodsInput.wood — historical field name, any resource). */
  wood: number
}

/** Buy a resource with gold at a buyable port — the ship sails the round trip and returns laden. */
export function useLaunchBuy() {
  const qc = useQueryClient()
  return useMutation<MissionDto, Error, BuyGoodsVars>({
    mutationFn: (vars) =>
      apiFetch<MissionDto>('/api/trade-runs/buy', { method: 'POST', body: JSON.stringify(vars) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] })
      qc.invalidateQueries({ queryKey: ['me', 'missions'] })
    },
  })
}

export interface ExploreVars {
  shipId: number
  islandId: number
}

export function useLaunchExploration() {
  const qc = useQueryClient()
  return useMutation<MissionDto, Error, ExploreVars>({
    mutationFn: (vars) =>
      apiFetch<MissionDto>('/api/explorations', { method: 'POST', body: JSON.stringify(vars) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] })
      qc.invalidateQueries({ queryKey: ['me', 'missions'] })
      qc.invalidateQueries({ queryKey: ['world', 'map'] })
    },
  })
}

export interface RaidVars {
  /** Ordered list of ship ids to send. */
  shipIds: number[]
  islandId: number
}

export interface RaidEstimateDto {
  winRate: number
  avgLosses: number
  /** p10 of ships lost per battle — the low end of the likely-losses range. */
  lossesLow: number
  /** p90 of ships lost per battle — the realistic worst end of the range. */
  lossesHigh: number
  fleetSize: number
  /**
   * Net loot a victory would pay at the player's CURRENT reputation, already
   * net of the crew wage and distributed by the target's type (comptoir→gold,
   * scierie→wood, distillerie→rhum, lair→mix). Backend-computed (no balance
   * constants in the front). Present whenever a fleet is selected.
   */
  loot?: { gold: number; wood: number; rhum: number }
  /** The crew's cut taken on the gold-equivalent total (gold), shown on its own line. */
  crewWage?: number
  /**
   * Gold-equivalent the player's reputation (method bonus + outlaw premium) adds
   * on top of the base loot value at this target. Shown as `base · réput +Y ·
   * total`. Server-computed; present whenever a fleet is selected.
   */
  reputationBonus?: number
  /**
   * Gold-equivalent the fleet's HULL POWER (Lever D) adds on top of the loot:
   * heavier hulls (frégate/galion/légendaire) plunder more than a brick swarm,
   * with diminishing returns and a cap. 0 for a brick-only fleet. Server-computed;
   * present whenever a fleet is selected (shown as a `flotte +Z` segment).
   */
  hullBonus?: number
  /** Raw loot value before any scaling, gold-equiv — the `base` of the breakdown. */
  base?: number
  /** Loot value after rep + hull, before the crew wage, gold-equiv — the `total`. */
  total?: number
}

/**
 * Win-rate estimate for a prospective raid — the backend runs a quick combat
 * simulation of the exact fleet (modules included) vs the target. Re-queries
 * when the selection changes.
 */
export function useRaidEstimate(shipIds: number[], islandId: number, enabled: boolean) {
  const sorted = [...shipIds].sort((a, b) => a - b)
  return useQuery<RaidEstimateDto>({
    queryKey: ['raid', 'estimate', islandId, sorted],
    queryFn: () =>
      apiFetch<RaidEstimateDto>('/api/raids/estimate', {
        method: 'POST',
        body: JSON.stringify({ shipIds, islandId }),
      }),
    enabled: enabled && shipIds.length > 0,
    staleTime: 60_000,
  })
}

/**
 * Win-rate estimate for a prospective PvP raid against a PLAYER island. Mirrors
 * {@see useRaidEstimate} but hits the scouted-gated `/api/pvp/raids/estimate`
 * endpoint and returns combat odds ONLY — NO loot (the defender's stock is
 * hidden, anti loot-scouting). Only enabled once the target has been scouted.
 */
export interface PvpRaidEstimateDto {
  winRate: number
  avgLosses: number
  /** p10 of ships lost per battle — the low end of the likely-losses range. */
  lossesLow: number
  /** p90 of ships lost per battle — the realistic worst end of the range. */
  lossesHigh: number
  fleetSize: number
}

export function usePvpRaidEstimate(shipIds: number[], islandId: number, scouted: boolean) {
  const sorted = [...shipIds].sort((a, b) => a - b)
  return useQuery<PvpRaidEstimateDto>({
    queryKey: ['pvp', 'raid', 'estimate', islandId, sorted],
    queryFn: () =>
      apiFetch<PvpRaidEstimateDto>('/api/pvp/raids/estimate', {
        method: 'POST',
        body: JSON.stringify({ shipIds, islandId }),
      }),
    // GATE: the endpoint 422s when not scouted — never call it before recon.
    enabled: scouted && shipIds.length > 0,
    staleTime: 60_000,
  })
}

export function useLaunchRaid() {
  const qc = useQueryClient()
  return useMutation<MissionDto, Error, RaidVars>({
    mutationFn: (vars) =>
      apiFetch<MissionDto>('/api/raids', { method: 'POST', body: JSON.stringify(vars) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] })
      qc.invalidateQueries({ queryKey: ['me', 'missions'] })
    },
  })
}

/* ── Chasse légendaire (fiche 32) ──────────────────────────────────────────
 *
 * ⚠️ AUCUN de ces DTO ne porte de chance de victoire, et ce n'est pas un oubli :
 * le serveur n'en expose pas (`HuntThreat` n'a pas de champ `winRate`, l'aperçu
 * ne rend que ses champs de pertes) et des tests verrouillent ces absences des
 * deux côtés. On dit au capitaine ce que la bête EST, jamais s'il va gagner.
 */

export interface HuntThreatDto {
  manoeuvre: number
  bordee: number
  abordage: number
  /** Le mur : son armure. Rien ne l'entame en dessous. */
  coreArmure: number
  /** L'armure la plus haute que SES coups atteignent. */
  offensivePierce: number
  coreHp: number
  /** Égal à `coreHp` : elle se regénère, chaque tentative la rencontre intacte. */
  coreMaxHp: number
  speciality: 'bordee' | 'abordage' | 'balanced'
}

export interface HuntArchetypeDto {
  key: string
  label: string
  sighting: string
  fleetHint: string
  maxAttempts: number
  titleLabel: string
}

export interface HuntDto {
  id: number
  archetypeKey: string
  /** Meilleur score : les PV les plus bas atteints. Informatif, rien ne se reporte. */
  coreHp: number
  coreMaxHp: number
  x: number
  y: number
  engagements: number
  attemptsLeft: number
  cornered: boolean
  felled: boolean
  escaped: boolean
  status: string
  threat: HuntThreatDto
}

export interface HuntBoardEntryDto {
  pseudo: string | null
  archetypeKey: string
  engagements: number
  felledAt: string | null
}

export interface KrakenStateResponse {
  /** Le gate du rite : posséder une coque légendaire à flot. */
  eligible: boolean
  archetype: HuntArchetypeDto
  prestigeAward: number
  hunt: HuntDto | null
  board: HuntBoardEntryDto[]
}

export function useKrakenState(enabled = true) {
  return useQuery<KrakenStateResponse>({
    queryKey: ['kraken', 'state'],
    queryFn: () => apiFetch<KrakenStateResponse>('/api/kraken'),
    enabled,
  })
}

export interface KrakenEstimateDto {
  fleetSize: number
  avgLosses: number
  lossesLow: number
  lossesHigh: number
  /** Ce que coûterait de reconstruire la flotte engagée : la mise réelle. */
  rebuildCost: { gold: number; wood: number; rhum: number }
  /** Toujours vrai : une flotte qui n'abat pas la bête ne rentre pas. */
  allOrNothing: boolean
  rhumNeeded: number
  durationSeconds: number
  engagements: number
  diveIndex: number
  attemptsLeft: number
  cornered: boolean
  threat: HuntThreatDto
  archetype: { key: string; label: string; sighting: string; fleetHint: string }
  /** Verdict MÉCANIQUE et binaire : aucune de tes phases ne perce son armure. */
  willRicochet: boolean
}

export function useKrakenEstimate(shipIds: number[], enabled: boolean) {
  const sorted = [...shipIds].sort((a, b) => a - b)
  return useQuery<KrakenEstimateDto>({
    queryKey: ['kraken', 'estimate', sorted],
    queryFn: () =>
      apiFetch<KrakenEstimateDto>('/api/kraken/estimate', {
        method: 'POST',
        body: JSON.stringify({ shipIds }),
      }),
    enabled: enabled && shipIds.length > 0,
    staleTime: 60_000,
  })
}

export function useEngageKraken() {
  const qc = useQueryClient()
  return useMutation<MissionDto, Error, { shipIds: number[] }>({
    mutationFn: (vars) =>
      apiFetch<MissionDto>('/api/kraken/engage', { method: 'POST', body: JSON.stringify(vars) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] })
      qc.invalidateQueries({ queryKey: ['me', 'missions'] })
      qc.invalidateQueries({ queryKey: ['kraken'] })
      qc.invalidateQueries({ queryKey: ['world', 'map'] })
    },
  })
}

export interface SalvageVars {
  shipIds: number[]
  destinationIslandId: number
}

export interface SalvageEstimateDto {
  durationSeconds: number
  rhumNeeded: number
  /** Remaining salvage charges on the target récif; null when unknown or unlimited. */
  salvageRemaining: number | null
  /**
   * Per ship-type sink probability ∈ [0,1], keyed by type value
   * ('brick'/'fregate'/'galion'/'legendary').
   */
  sinkRates: Record<string, number>
  /** Best-case reward assuming all ships survive the salvage run. */
  reward: { gold: number; wood: number; rhum: number }
}

/**
 * Cost/risk estimate for a prospective salvage run to a récif. Re-queries when
 * the ship selection or destination changes.
 */
export function useSalvageEstimate(shipIds: number[], destinationIslandId: number, enabled: boolean) {
  const sorted = [...shipIds].sort((a, b) => a - b)
  return useQuery<SalvageEstimateDto>({
    queryKey: ['salvage', 'estimate', destinationIslandId, sorted],
    queryFn: () =>
      apiFetch<SalvageEstimateDto>('/api/recif/estimate', {
        method: 'POST',
        body: JSON.stringify({ shipIds, destinationIslandId }),
      }),
    enabled: enabled && shipIds.length > 0,
    staleTime: 60_000,
  })
}

export function useLaunchSalvage() {
  const qc = useQueryClient()
  return useMutation<MissionDto, Error, SalvageVars>({
    mutationFn: (vars) =>
      apiFetch<MissionDto>('/api/recif/salvage', { method: 'POST', body: JSON.stringify(vars) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['world', 'map'] })
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] })
      qc.invalidateQueries({ queryKey: ['me', 'missions'] })
    },
  })
}

export interface SmuggleVars {
  /** Convoy ship ids — the cargo sails as a fleet (interception risk scales loss). */
  shipIds: number[]
  destinationIslandId: number
  wood: number
  rum: number
}

export interface SmuggleEstimateDto {
  /** Interception probability ∈ [0,1] — outlaw notoriety × the target's danger. */
  interceptionRisk: number
  /**
   * Sale breakdown at the player's CURRENT reputation. `base` is the legal sale
   * value (destination × distance, no rep), `reputationBonus` the gold the outlaw
   * margin (∝ |loyalty| × danger) + method affinity add, `moduleBonus` the gold
   * the convoy's best Faux-fond adds (0 for a bare convoy — same multiplier the
   * resolver credits), `total` the rounded sum. All server-computed (no balance
   * constants in the front).
   */
  breakdown: { base: number; reputationBonus: number; moduleBonus: number; total: number }
  /**
   * Composition of the Marine patrol the haul would draw (grouped by ship type),
   * driven by the butin's gold value — mirror of the commerce `ambushFleet`.
   */
  interceptionFleet: { shipType: string; count: number }[]
  /**
   * Advisory win-odds tier of the SELECTED convoy vs that patrol (faible = the
   * convoy dominates, forte = outgunned) — mirror of the commerce `ambushThreat`.
   */
  interceptionThreat: 'faible' | 'modérée' | 'forte'
}

/**
 * Interception risk + sale breakdown for a prospective smuggling run. Re-queries
 * when the convoy, destination or cargo changes. Server is authoritative — the
 * front only displays the returned numbers.
 */
export function useSmuggleEstimate(
  shipIds: number[],
  destinationIslandId: number,
  wood: number,
  rum: number,
  enabled: boolean,
) {
  const sorted = [...shipIds].sort((a, b) => a - b)
  return useQuery<SmuggleEstimateDto>({
    queryKey: ['smuggle', 'estimate', destinationIslandId, sorted, wood, rum],
    queryFn: () =>
      apiFetch<SmuggleEstimateDto>('/api/smuggle/estimate', {
        method: 'POST',
        body: JSON.stringify({ shipIds, destinationIslandId, wood, rum }),
      }),
    enabled: enabled && shipIds.length > 0 && wood + rum > 0,
    staleTime: 60_000,
  })
}

export function useLaunchSmuggle() {
  const qc = useQueryClient()
  return useMutation<MissionDto, Error, SmuggleVars>({
    mutationFn: (vars) =>
      apiFetch<MissionDto>('/api/smuggle', { method: 'POST', body: JSON.stringify(vars) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] })
      qc.invalidateQueries({ queryKey: ['me', 'missions'] })
    },
  })
}

export interface BlackMarketVars {
  /** Convoy ship ids — the gold sails as a fleet (interception risk scales loss). */
  shipIds: number[]
  destinationIslandId: number
  /** Which good to buy with gold (BlackMarketInput.resource — IslandSpecialty). */
  resource: 'wood' | 'rhum'
  /** Units to buy with gold. */
  amount: number
}

/**
 * Marché noir (Outlaw Lot 4): the BUY-side mirror of contrebande. The convoy
 * spends GOLD upfront at a pirate lair to buy a resource, at the same
 * interception risk as a sell run. Hits the buy endpoint on the smuggle route.
 */
export function useLaunchBlackMarket() {
  const qc = useQueryClient()
  return useMutation<MissionDto, Error, BlackMarketVars>({
    mutationFn: (vars) =>
      apiFetch<MissionDto>('/api/smuggle/buy', { method: 'POST', body: JSON.stringify(vars) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] })
      qc.invalidateQueries({ queryKey: ['me', 'missions'] })
    },
  })
}

export interface BlackMarketEstimateDto {
  /**
   * Server-AUTHORITATIVE gold cost to buy this resource+amount at this lair, at the
   * player's CURRENT reputation (the rep-driven outlaw discount is NOT mirrored in
   * the front). Equals the gold the launch actually debits — the front gates the
   * launch on affordable gold against THIS number, never a client computation.
   */
  goldCost: number
  /** Interception probability ∈ [0,1] — outlaw notoriety × the target's danger. */
  interceptionRisk: number
  /** Composition of the Marine patrol the gold sailed would draw (grouped by ship type). */
  interceptionFleet: { shipType: string; count: number }[]
  /** Advisory win-odds tier of the SELECTED convoy vs that patrol. */
  interceptionThreat: 'faible' | 'modérée' | 'forte'
}

/**
 * Server-authoritative gold-cost preview for a prospective marché noir BUY. The
 * outlaw discount is rep-driven (never client-computable) AND a pirate lair has no
 * `buyOptions` base price, so unlike the legal commerce buy the front CANNOT mirror
 * the cost — it asks the server. Re-queries when the convoy, destination, resource
 * or amount changes. Display + gating only; the launch recomputes the debit.
 */
export function useBlackMarketEstimate(
  shipIds: number[],
  destinationIslandId: number,
  resource: 'wood' | 'rhum',
  amount: number,
  enabled: boolean,
) {
  const sorted = [...shipIds].sort((a, b) => a - b)
  return useQuery<BlackMarketEstimateDto>({
    queryKey: ['blackMarket', 'estimate', destinationIslandId, sorted, resource, amount],
    queryFn: () =>
      apiFetch<BlackMarketEstimateDto>('/api/smuggle/buy/estimate', {
        method: 'POST',
        body: JSON.stringify({ shipIds, destinationIslandId, resource, amount }),
      }),
    enabled: enabled && shipIds.length > 0 && amount > 0,
    staleTime: 60_000,
  })
}

// Sell prices mirror the backend MarketPrices. Kept in sync manually for now.
export const MARKET_PRICES = {
  goldPerWood: 2,
  goldPerRum: 5,
  destinationMultiplier: {
    marine_port: 1.0,
    merchant_outpost: 1.3,
    production: 0.9,
  } as Record<string, number>,
  // Mirrors MarketPrices::DISTANCE_PREMIUM_PER_LEAGUE — longer routes pay more.
  distancePremiumPerLeague: 0.03,
} as const

/**
 * Estimated gold cost to buy a resource at a buyable port — mirrors the server's
 * ResourceExchange buy cost. Resource-agnostic: `basePricePerUnit` is the
 * selected option's pre-distance price (MapIslandDto.buyOptions[].basePrice); the
 * distance premium is applied here, same as the sell side. Estimate only — the
 * server recomputes the exact debit at launch.
 */
export function estimateBuyCost(units: number, basePricePerUnit: number, oneWayLeagues: number): number {
  const premium = 1 + MARKET_PRICES.distancePremiumPerLeague * Math.max(0, oneWayLeagues)
  return Math.ceil(Math.max(0, units) * basePricePerUnit * premium)
}

/** Crew's flat share of mission gold — mirrors the backend CrewWage (gold sink). */
export const CREW_WAGE_RATE = 0.2
export function crewWageCut(gold: number): number {
  return Math.round(Math.max(0, gold) * CREW_WAGE_RATE)
}
export function netAfterCrewWage(gold: number): number {
  return Math.max(0, gold) - crewWageCut(gold)
}

// Ship nautical speeds mirror ShipBlueprint::nauticalSpeedPerHour
export const SHIP_SPEED_PER_HOUR: Record<string, number> = {
  brick: 8,
  fregate: 6,
  galion: 4,
  legendary: 7,
}

// ---------- Forge ----------

export interface ForgeTechDto {
  key: string
  branch: string
  branchLabel: string
  label: string
  description: string
  cost: { gold: number; wood: number; rhum: number }
  /** Estimated build time (seconds) the research will take — shown on the tile. */
  durationSeconds: number
  requires: string | null
  /** UI icon key — the characteristic improved (StatIcon name or economy key). */
  icon: string
  /** Already unlocked by the player this season. */
  researched: boolean
  /** The live timed research currently building this tech. */
  inProgress: boolean
  /** Prerequisite met AND no research running (serial) → can be launched now. */
  available: boolean
}

export interface ForgeActiveResearch {
  techKey: string
  techLabel: string
  branch: string
  branchLabel: string
  resolvesAt: string
}

export interface ForgeStateResponse {
  techs: ForgeTechDto[]
  researchedKeys: string[]
  activeResearch: ForgeActiveResearch | null
}

export function useForge() {
  return useQuery<ForgeStateResponse>({
    queryKey: ['me', 'forge'],
    queryFn: () => apiFetch<ForgeStateResponse>('/api/me/forge'),
  })
}

export function useResearchForge() {
  const qc = useQueryClient()
  return useMutation<ForgeStateResponse, Error, string>({
    mutationFn: (techKey) =>
      apiFetch<ForgeStateResponse>('/api/me/forge/research', {
        method: 'POST',
        body: JSON.stringify({ techKey }),
      }),
    onSuccess: (state) => {
      qc.setQueryData(['me', 'forge'], state)
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] }) // gold/wood/rhum were debited
    },
  })
}

// ---------- Grand Chantier ----------

export type ChantierPathKey = 'or' | 'bois'
export type ChantierTierState = 'erected' | 'open' | 'next' | 'locked'

export interface ChantierWayDto {
  path: ChantierPathKey
  label: string
  description: string
  /** Which scarce resource this way taps, on top of the shared rhum. */
  secondaryResource: 'gold' | 'wood'
  cost: { gold: number; wood: number; rhum: number }
  /** Masonry once the quote is covered. The wood way takes 5/3 of the gold way. */
  durationSeconds: number
  /** False when the season closes before the masonry could finish: the way is CLOSED. */
  reachable: boolean
  latestStart: string
}

export interface ChantierTierDto {
  index: number
  key: string
  label: string
  description: string
  rhumCost: number
  /** Spent in gold on the Or way, in wood on the Bois way. Never both. */
  rareCost: number
  totalUnits: number
  prestige: number
  stoneQuota: number
  /** The island's stone yard holds enough to open this tier. */
  stoneReady: boolean
  stoneMissing: number
  stonePercent: number
  state: ChantierTierState
  erectedPath: ChantierPathKey | null
  /** Neither way can finish before the reset. */
  closedForSeason: boolean
  ways: ChantierWayDto[]
}

export interface ChantierOpenTierDto {
  tierIndex: number
  tierKey: string
  tierLabel: string
  path: ChantierPathKey
  pathLabel: string
  paid: { gold: number; wood: number; rhum: number }
  remaining: { gold: number; wood: number; rhum: number }
  quotePercent: number
  quoteCovered: boolean
  /** Once true the tier accepts no further instalment: the countdown runs. */
  masonryStarted: boolean
  resolvesAt: string | null
  durationSeconds: number
}

export interface ChantierSeasonDto {
  name: string
  endsAt: string
  daysRemaining: number
  week: number
  totalWeeks: number
}

export interface ChantierStateResponse {
  eligible: boolean
  /** Player-facing reason the screen is locked, or null when it is open. */
  lockReason: string | null
  season: ChantierSeasonDto
  erectedCount: number
  totalTiers: number
  crowned: boolean
  stoneStock: number
  tiers: ChantierTierDto[]
  openTier: ChantierOpenTierDto | null
  /** Le convoi de carrière en mer, s'il y en a un. Un seul à la fois. */
  convoy: ChantierConvoyDto | null
}

export interface ChantierConvoyDto {
  /** Tonnage figé au départ : ce qui sera déchargé au retour. */
  stone: number
  resolvesAt: string
}

export function useChantier() {
  return useQuery<ChantierStateResponse>({
    queryKey: ['me', 'chantier'],
    queryFn: () => apiFetch<ChantierStateResponse>('/api/me/chantier'),
  })
}

export function useOpenChantierTier() {
  const qc = useQueryClient()
  return useMutation<ChantierStateResponse, Error, ChantierPathKey>({
    mutationFn: (path) =>
      apiFetch<ChantierStateResponse>('/api/me/chantier/tier', {
        method: 'POST',
        body: JSON.stringify({ path }),
      }),
    onSuccess: (state) => {
      qc.setQueryData(['me', 'chantier'], state)
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] }) // le chantier de pierre a été entamé
    },
  })
}

export interface ChantierInstalment {
  gold?: number
  wood?: number
  rhum?: number
}

export function useContributeChantier() {
  const qc = useQueryClient()
  return useMutation<ChantierStateResponse, Error, ChantierInstalment>({
    mutationFn: (instalment) =>
      apiFetch<ChantierStateResponse>('/api/me/chantier/tier/contribute', {
        method: 'POST',
        body: JSON.stringify({
          gold: instalment.gold ?? 0,
          wood: instalment.wood ?? 0,
          rhum: instalment.rhum ?? 0,
        }),
      }),
    onSuccess: (state) => {
      qc.setQueryData(['me', 'chantier'], state)
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] }) // versement débité
      qc.invalidateQueries({ queryKey: ['me', 'profile'] }) // prestige, si le palier sort
    },
  })
}

export interface QuarryConvoyResponse {
  missionId: number
  stone: number
  resolvesAt: string | null
}

/**
 * Arme un convoi vers la carrière. Le tonnage n'est jamais envoyé par le client :
 * le serveur lit la capacité de charge réelle de chaque coque. Le front n'en
 * calcule qu'un miroir d'affichage, via `tradeCargoCap`.
 */
export interface QuarryPreviewResponse {
  /** Tonnage que le convoi ramènera : somme des cales, puis multiplicateur de Forge. */
  stone: number
  durationSeconds: number
  rhumCost: number
  fleetSize: number
  fleetCap: number
  target: { x: number; y: number }
}

/**
 * L'aperçu d'un convoi, calculé par le SERVEUR avec le code même du lancement.
 *
 * On ne recalcule pas la durée côté client : elle dépend de la coque la plus lente,
 * de ses modules de gréement et de la tech de voiles de la Forge, et un miroir
 * incomplet mentirait au capitaine. Le tonnage, lui, a un miroir exact
 * (`tradeCargoCap`), mais on affiche celui du serveur dès qu'il est là.
 */
export function useQuarryPreview() {
  return useMutation<QuarryPreviewResponse, Error, number[]>({
    mutationFn: (shipIds) =>
      apiFetch<QuarryPreviewResponse>('/api/me/chantier/quarry/preview', {
        method: 'POST',
        body: JSON.stringify({ shipIds }),
      }),
  })
}

export function useQuarryConvoy() {
  const qc = useQueryClient()
  return useMutation<QuarryConvoyResponse, Error, number[]>({
    mutationFn: (shipIds) =>
      apiFetch<QuarryConvoyResponse>('/api/me/chantier/quarry/convoy', {
        method: 'POST',
        body: JSON.stringify({ shipIds }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] }) // rations débitées, flotte en mer
      qc.invalidateQueries({ queryKey: ['me', 'missions'] })
      qc.invalidateQueries({ queryKey: ['me', 'chantier'] })
    },
  })
}

// -------- Notifications (centre de notifications, Lot 1) --------

export interface NotificationDto {
  id: number
  type:
    | 'mission_completed'
    | 'raid_resolved'
    | 'forge_research_completed'
    | 'quest_offered'
    | 'quest_ready'
    | 'pvp_raid_resolved'
    | 'island_scouted'
    | 'scout_repelled'
    | 'reputation_module_unlocked'
    | 'changelog'
    | 'streak_started'
    | 'streak_milestone'
    | 'streak_shore_leave'
    | 'cosmetic_unlocked'
    | 'mobilization_ended'
    | 'chantier_tier_erected'
  title: string
  body: string
  payload: Record<string, unknown>
  createdAt: string
  read: boolean
}

export interface NotificationsResponse {
  notifications: NotificationDto[]
  unread: number
}

export function useNotifications(enabled = true) {
  return useQuery<NotificationsResponse>({
    queryKey: ['me', 'notifications'],
    queryFn: () => apiFetch<NotificationsResponse>('/api/me/notifications'),
    enabled,
  })
}

export function useMarkNotificationRead() {
  const qc = useQueryClient()
  return useMutation<{ unread: number }, Error, number>({
    mutationFn: (id) =>
      apiFetch<{ unread: number }>(`/api/me/notifications/${id}/read`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'notifications'] }),
  })
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient()
  return useMutation<{ unread: number }, Error, void>({
    mutationFn: () =>
      apiFetch<{ unread: number }>('/api/me/notifications/read-all', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'notifications'] }),
  })
}

// -------- PvP — scout / raid between players (M10) --------

/**
 * A single player-vs-player raid as seen by either participant in their history.
 * Mirrors PlayerRaidOutput: carries only the participants' own data — never the
 * defender's resource stock (anti loot-scouting). `combat` drives the replay and
 * `outcome` are null while the fleet is still traveling.
 */
export interface PlayerRaidDto {
  id: number
  status: 'traveling' | 'resolved' | 'season_over'
  /** ISO (ATOM) — when the raid resolves. */
  scheduledAt: string | null
  /** ISO (ATOM) — when the fleet set sail. With scheduledAt, gives the travel duration. */
  createdAt: string
  /** ISO (ATOM) — when it actually resolved, null while traveling. */
  resolvedAt: string | null
  /**
   * Resolution verdict, null while traveling. `immune` = the fleet arrived on a
   * target that had been plundered by someone ELSE in the meantime, so it stood
   * down without fighting ({@see PlayerRaidService::resolve}). It was missing
   * from this union while the backend already emitted it, and since these types
   * are hand-written no compiler caught the branches that then fell through to
   * "still at sea".
   */
  outcome: 'plundered' | 'repelled' | 'immune' | 'season_over' | null
  /** Plundered loot (rendered as icons in the report). */
  loot: { gold: number; wood: number; rhum: number }
  attackerPseudo: string | null
  defenderPseudo: string | null
  defenderIslandName: string | null
  /** Target island id — lets the map draw the raid route. */
  defenderIslandId: number
  /** Size of the attacking fleet (frozen at launch). */
  shipCount: number
  /** Composition de la flotte attaquante (gelée au lancement), groupée par type. */
  fleet: FleetTypeCount[]
  /** Full fleet-battle replay (M6-A5 format), null while traveling. */
  combat: CombatReplayDto | null
}

export interface PvpRaidsResponse {
  sent: PlayerRaidDto[]
  received: PlayerRaidDto[]
}

/**
 * Launch an éclaireur FLEET (1-5 ships) against a player island — reveals its
 * defending fleet (intel) and notifies the target. The contest reads the summed
 * recon depth (Longue-vue modules) across the fleet vs the target's Watchtower.
 * Returns the scouting Mission (a patrol-style mission resolved by Messenger).
 * Refreshes the fleet (ships are now busy) and the map (the target becomes
 * `scouted`, exposing its threat readout).
 */
export function useScout() {
  const qc = useQueryClient()
  return useMutation<MissionDto, Error, { shipIds: number[]; islandId: number }>({
    mutationFn: (vars) =>
      apiFetch<MissionDto>('/api/pvp/scout', { method: 'POST', body: JSON.stringify(vars) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] })
      qc.invalidateQueries({ queryKey: ['me', 'missions'] })
      qc.invalidateQueries({ queryKey: ['world', 'map'] })
    },
  })
}

/**
 * Launch a raid fleet against a player island. Returns the freshly-created
 * PlayerRaid (still traveling — resolution is scheduled via Messenger). Refreshes
 * the fleet (ships now at sea), the map (immunity / cooldown flags shift), and the
 * raid history.
 */
export function useLaunchPvpRaid() {
  const qc = useQueryClient()
  return useMutation<PlayerRaidDto, Error, { shipIds: number[]; islandId: number }>({
    mutationFn: (vars) =>
      apiFetch<PlayerRaidDto>('/api/pvp/raids', { method: 'POST', body: JSON.stringify(vars) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] })
      qc.invalidateQueries({ queryKey: ['world', 'map'] })
      qc.invalidateQueries({ queryKey: ['pvp', 'raids'] })
    },
  })
}

/** This captain's PvP raid history, split into raids they sent and received. */
export function usePvpRaids() {
  return useQuery<PvpRaidsResponse>({
    queryKey: ['pvp', 'raids'],
    queryFn: () => apiFetch<PvpRaidsResponse>('/api/pvp/raids'),
  })
}

// ——— Diplomatie & corruption — « La table des négociations » ———

export interface DiplomacyCampDto {
  maxPoints: number
  /** Coûts CUMULATIFS : costs[i] = total en or pour i+1 points. */
  costs: number[]
}

export interface DiplomacyStateDto {
  loyalty: number
  availableAt: string | null
  camps: { diplomacy: DiplomacyCampDto; corruption: DiplomacyCampDto }
}

export interface NegotiateResultDto extends DiplomacyStateDto {
  /** Or réellement débité par la démarche (réponse du POST uniquement). */
  goldSpent: number
}

export type DiplomacyDirection = 'diplomacy' | 'corruption'

export function useDiplomacy(enabled = true) {
  return useQuery<DiplomacyStateDto>({
    queryKey: ['me', 'diplomacy'],
    queryFn: () => apiFetch<DiplomacyStateDto>('/api/diplomacy'),
    enabled,
  })
}

export function useNegotiate() {
  const qc = useQueryClient()
  const { refreshUser } = useAuth()
  return useMutation<NegotiateResultDto, Error, { direction: DiplomacyDirection; points: number }>({
    mutationFn: (payload) =>
      apiFetch<NegotiateResultDto>('/api/diplomacy', { method: 'POST', body: JSON.stringify(payload) }),
    onSuccess: (data) => {
      // Le POST renvoie l'état à jour → on seed le cache avec les champs d'état seuls.
      qc.setQueryData<DiplomacyStateDto>(['me', 'diplomacy'], {
        loyalty: data.loyalty,
        availableAt: data.availableAt,
        camps: data.camps,
      })
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] }) // or débité
      qc.invalidateQueries({ queryKey: ['me', 'profile'] }) // réputation (page Capitaine)
      void refreshUser() // user.reputation du AuthContext (header, dashboard)
    },
  })
}

// -------- Récits — îles mystérieuses (Phase 3 story engine) --------

/** Authored risk hint on a story choice — the ONLY consequence metadata exposed. */
export type StoryRisk = 'low' | 'medium' | 'high'

export interface StoryChoiceDto {
  index: number
  label: string
  playable: boolean
  lock: string | null
  /** Risk indicator (jets pouvant couler une coque / perdre des ressources) — null = choix sûr. */
  risk: StoryRisk | null
}

/** Lifecycle of an expedition (mirrors backend App\Enum\ExpeditionState). */
export type ExpeditionState = 'traveling' | 'readable' | 'returning' | 'done'

export interface StoryViewDto {
  id: number
  title: string
  islandName: string
  status: string
  state: ExpeditionState
  /** Current node id — a stable key for replaying the reader's entrance animation per section. */
  nodeId: string
  text: string
  isEnding: boolean
  endingLabel: string | null
  choices: StoryChoiceDto[]
  reward: { gold: number; wood: number; rhum: number } | null
  arrivesAt: string
  returnsAt: string | null
}

export interface StoryListItemDto {
  id: number
  title: string
  islandName: string
  status: string
  state: ExpeditionState
  endingLabel: string | null
  arrivesAt: string
  returnsAt: string | null
  /** Route fields — let the world map draw the trajectory to the island. */
  destinationIslandId: number | null
  startedAt: string
  shipCount: number
}

export interface RecitsResponse {
  recits: StoryListItemDto[]
}

/** Advance response: { recit: StoryViewDto, applied: {...} } — key is "recit" (not "view"). */
export interface StoryAdvanceDto {
  recit: StoryViewDto
  applied: { gold: number; wood: number; rhum: number; loyalty: number; method: number; shipLost: boolean }
}

export function useRecits(enabled = true) {
  return useQuery<RecitsResponse>({
    queryKey: ['me', 'recits'],
    queryFn: () => apiFetch<RecitsResponse>('/api/me/recits'),
    enabled,
  })
}

export interface LaunchExpeditionVars {
  islandId: number
  shipIds: number[]
}

/** Launch an expedition: send a fleet to a mysterious island. Returns the new récit view. */
export function useLaunchExpedition() {
  const qc = useQueryClient()
  return useMutation<StoryViewDto, Error, LaunchExpeditionVars>({
    mutationFn: ({ islandId, shipIds }) =>
      apiFetch<StoryViewDto>('/api/me/recits', {
        method: 'POST',
        body: JSON.stringify({ islandId, shipIds }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'recits'] })
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] }) // ships now InMission
      qc.invalidateQueries({ queryKey: ['world', 'map'] }) // island now charted
    },
  })
}

export function useRecit(id: number, enabled = true) {
  return useQuery<StoryViewDto>({
    queryKey: ['me', 'recit', id],
    queryFn: () => apiFetch<StoryViewDto>(`/api/me/recits/${id}`),
    enabled,
  })
}

export function useAdvanceRecit() {
  const qc = useQueryClient()
  const { refreshUser } = useAuth()
  return useMutation<StoryAdvanceDto, Error, { id: number; choiceIndex: number }>({
    mutationFn: ({ id, choiceIndex }) =>
      apiFetch<StoryAdvanceDto>(`/api/me/recits/${id}/advance`, {
        method: 'POST',
        body: JSON.stringify({ choiceIndex }),
      }),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['me', 'recit', id] })
      qc.invalidateQueries({ queryKey: ['me', 'recits'] })
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] })
      void refreshUser()
    },
  })
}

// -------- Classements & profil public (Leaderboards) --------

/** One row in a leaderboard table. Mirrors the backend LeaderboardsOutput row. */
export interface LeaderboardRow {
  place: number
  pseudo: string
  faction: ReputationDto['faction']
  /** Notoriety tier 0..5 — fed straight to `<ReputationIcon rank={...} />`. */
  reputationRank: number
  /** Apparence cosmétique publique (mini-portrait + titre équipé) — null si jamais configurée. */
  appearance: AppearanceDto | null
  value: number
}

/** The viewer's own standing on a board, when ranked. `toCatch` = gap to the place above (null at #1). */
export interface LeaderboardMe {
  place: number
  value: number
  toCatch: number | null
}

/** Loyal/outlaw sub-filter keys for the splittable boards (terreur, negoce). */
export type BoardAlignment = 'loyal' | 'outlaw'

/** One alignment sub-ranking: its own top-10 + the viewer's standing within it. */
export interface LeaderboardVariant {
  rows: LeaderboardRow[]
  me: LeaderboardMe | null
}

export interface LeaderboardBoard {
  key: 'fortune' | 'terreur' | 'negoce' | 'legendes' | 'chantier'
  label: string
  /** `paliers` = paliers du Grand Phare érigés (le serveur trie sur un score composé). */
  metric: 'pts' | 'gold' | 'prestige' | 'paliers'
  /** True for the cross-season Légendes board (eternal prestige ranking). */
  eternal: boolean
  /** The default "Tout" ranking (all loot origins combined). */
  rows: LeaderboardRow[]
  me: LeaderboardMe | null
  /**
   * Present only on boards that carry the alignment filter (terreur, negoce):
   * loot re-tallied by the origin of the deed. "Tout" reuses rows/me above.
   */
  alignments?: {
    loyal: LeaderboardVariant
    outlaw: LeaderboardVariant
  } | null
}

export interface LeaderboardsResponse {
  seasonName: string
  week: number
  totalWeeks: number
  boards: LeaderboardBoard[]
}

/** All leaderboards for the current season (+ the eternal Légendes board). */
export function useLeaderboards() {
  return useQuery<LeaderboardsResponse>({
    queryKey: ['leaderboards'],
    queryFn: () => apiFetch<LeaderboardsResponse>('/api/leaderboards'),
    refetchOnWindowFocus: true,
  })
}

/** A captain's notable feat (faits d'armes) on their public profile. */
export interface FaitDArmes {
  key: string
  title: string
  description: string
}

export interface PublicCaptainSeasonStats {
  plunder: number
  commerceGold: number
  raidsWon: number
  raidsRepelled: number
  islandsDiscovered: number
  shipsBuilt: number
}

/** A board a captain places on, shown as a chip on their public profile. */
export interface PublicCaptainBoardPlace {
  key: string
  label: string
  place: number
}

/** A captain's public profile. Mirrors the backend PublicCaptainOutput. */
export interface PublicCaptainDto {
  pseudo: string
  createdAt: string
  prestigePoints: number
  prestigeTitle: string
  /** Label du titre de série de connexions le plus élevé (permanent, compte), null si aucun. */
  streakTitle: string | null
  /** Apparence cosmétique (cabine) — null tant que le joueur n'a rien configuré. */
  appearance: AppearanceDto | null
  reputation: ReputationDto
  season: SeasonDto | null
  faitsDArmes: FaitDArmes[]
  seasonStats: PublicCaptainSeasonStats
  boards: PublicCaptainBoardPlace[]
}

/** A captain's public profile, fetched by pseudo. Disabled until a pseudo is provided. */
export function usePublicCaptain(pseudo: string | undefined) {
  return useQuery<PublicCaptainDto>({
    queryKey: ['captain', pseudo],
    queryFn: () => apiFetch<PublicCaptainDto>(`/api/captains/${encodeURIComponent(pseudo as string)}`),
    enabled: !!pseudo,
  })
}

// -------- Palmarès des saisons closes (fiche 31, lot 31d) --------

/** Une place gravée au podium d'une saison close. */
export interface PastSeasonRow {
  rank: number
  pseudo: string
  score: number
}

export interface PastSeasonBoard {
  /** Les quatre boards SAISONNIERS. `legendes` est éternel et n'a pas de palmarès. */
  key: 'fortune' | 'terreur' | 'negoce' | 'chantier'
  label: string
  /** Même vocabulaire de métrique que les classements vivants. */
  metric: 'pts' | 'gold' | 'prestige' | 'paliers'
  /** Le podium seul : au plus trois lignes, rang croissant. */
  rows: PastSeasonRow[]
}

export interface PastSeason {
  id: number
  name: string
  startedAt: string
  endsAt: string
  /** Un board absent est un board que personne n'a marqué cette saison-là. */
  boards: PastSeasonBoard[]
}

export interface PastSeasonsResponse {
  seasons: PastSeason[]
}

/**
 * Le palmarès des saisons closes, gravé une fois pour toutes au couperet. Il ne
 * peut bouger qu'au reset suivant : on le garde frais longtemps plutôt que de le
 * refetcher au moindre focus.
 */
export function usePastSeasons() {
  return useQuery<PastSeasonsResponse>({
    queryKey: ['leaderboards', 'seasons'],
    queryFn: () => apiFetch<PastSeasonsResponse>('/api/leaderboards/seasons'),
    staleTime: 5 * 60 * 1000,
  })
}

// -------- Carte au trésor (login streaks, fiche 25) --------

/** Clés des titres permanents de série (compte, pas saison). */
export type StreakTitleKey = 'loup_de_mer' | 'habitue_du_bord' | 'fidele_de_corsalia'

export interface StreakOwnedTitleDto {
  key: StreakTitleKey
  label: string
}

/** Prochain palier de la série — TOUJOURS présent (boucle infinie au-delà de 30 j). */
export interface StreakMilestoneDto {
  /** Prochain palier strictement > count (1, 3, 7, 14, 30, 45, 60, …). */
  day: number
  prestige: number
  gold: number
  wood: number
  titleLabel: string | null
}

/** Mirrors GET /api/me/streak (StreakService::statusFor). */
export interface StreakDto {
  /** État brut persisté — register/login/GET ne comptent jamais. */
  count: number
  /** Le jour actif le plus récent est aujourd'hui (jour calendaire Europe/Paris). */
  todayDone: boolean
  /** Escales en banque (0..2) — chacune absorbe automatiquement un jour manqué. */
  shoreLeaves: number
  nextMilestone: StreakMilestoneDto
  /**
   * Le palier dont le coffre a été décroché AUJOURD'HUI (le compte tombe pile
   * dessus, jour tracé), sinon null → non-null, la carte fait briller ce coffre.
   */
  reachedMilestone: StreakMilestoneDto | null
  /**
   * Jour du palier juste sous le coffre AFFICHÉ (le décroché s'il y en a un,
   * sinon le prochain). La frise va de segmentStartDay+1 au coffre : elle repart
   * à zéro à chaque palier, au lieu d'une fenêtre glissante des 7 derniers jours.
   */
  segmentStartDay: number
  /** Titres permanents possédés, triés par rang croissant. */
  titles: StreakOwnedTitleDto[]
}

/**
 * Série de connexions du capitaine. La clé ['streak'] est invalidée GLOBALEMENT
 * par la MutationCache du QueryClient (voir api/queryClient.ts) : toute requête
 * mutante réussie peut « tracer la croix du jour », donc chip + carte se
 * rafraîchissent dès la première action du jour, sans câblage par mutation.
 */
export function useStreakQuery(enabled = true) {
  return useQuery<StreakDto>({
    queryKey: ['streak'],
    queryFn: () => apiFetch<StreakDto>('/api/me/streak'),
    enabled,
  })
}

// -------- Nouveautés (journal de bord public, /nouveautes) --------

/**
 * Un bloc de contenu d'une nouveauté — union discriminée sur `type`.
 * Délibérément PAS du HTML brut : le backend n'envoie que du texte, que la page
 * rend en éléments React. Aucun `dangerouslySetInnerHTML`, donc aucune surface XSS.
 */
export type NewsBlock =
  | { type: 'p'; text: string }
  | { type: 'h'; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'stats'; items: { label: string; value: string }[] }

export interface NewsPost {
  /** Identifiant stable, aussi utilisé comme ancre : /nouveautes#<slug>. */
  slug: string
  title: string
  /** Jour de publication, format ISO court (YYYY-MM-DD). */
  publishedAt: string
  teaser: string
  blocks: NewsBlock[]
}

export interface NewsResponse {
  /** Trié du plus récent au plus ancien par le backend — la page respecte cet ordre. */
  posts: NewsPost[]
}

/**
 * Journal de bord public (GET /api/news). Endpoint SANS auth : `apiFetch` ne
 * pose l'en-tête Authorization que s'il y a un jeton, donc la requête marche
 * telle quelle pour un visiteur déconnecté.
 */
export function useNews() {
  return useQuery<NewsResponse>({
    queryKey: ['news'],
    queryFn: () => apiFetch<NewsResponse>('/api/news'),
  })
}
